import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Tabs,
  Row,
  Col,
  List,
  Empty,
  Spin,
  message,
  Switch,
  Tooltip,
  DatePicker,
  Popconfirm,
  Collapse,
  Checkbox,
  Alert,
  Badge,
  Timeline,
  Divider,
  Descriptions,
} from 'antd';

import {
  EditOutlined,
  HistoryOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CodeOutlined,
  ApiOutlined,
  RobotOutlined,
  EyeOutlined,
  BranchesOutlined,
  SaveOutlined,
  CopyOutlined,
  DeleteOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  FileTextOutlined,
  SettingOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
} from '@ant-design/icons';

import { getUserRole, isAuthenticated } from '../../../../auth';
import { v2PromptsApi, v2Step1AnalysisApi, v2StylesApi } from '../../../../api';
import moment from 'moment-timezone';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;
const { Panel } = Collapse;

// プロンプト管理カテゴリー
const GENERATION_FLOW = [
  {
    id: 'seo_aio_analysis',
    name: 'SEO/AIO分析',
    description:
      'DataForSEO統合分析・競合見出し抽出・トピック処理・語句分析・ヒートマップ生成・分析結果統合',
    prompts: [
      'topic_extraction_raw',
      'topic_unification_mece',
      'site_topic_analysis',
      'terms_normalization',
      'keyword_lsi_analysis',
      'site_summary_generation',
      'serps_evaluation_generation',
      'topic_landscape_analysis',
      'aio_content_strategy_generation',
      'search_intent_deepdive',
    ],
  },
  {
    id: 'style_management',
    name: 'スタイル管理',
    description: '記事スタイルの解析/保存/再利用（YAMLレシピ化）',
    prompts: ['style_analysis_yaml_v1'],
  },
  {
    id: 'content_generation_step2',
    name: 'コンテンツ生成_STEP2',
    description: '検索意図分析・情報収集・アウトライン候補生成',
    prompts: [
      'simple_search_intent_analysis',
      'latest_info_collection',
      'content_generation_outline_candidates_v2',
      'required_info_extraction',
      'content_generation_outline_comprehensive',
      'content_generation_outline_deepdive',
      'content_generation_outline_creative',
      // [NEW] 新4ステップフロー
      'outline_strategic_design_v2',
      'outline_research_execution_v2',
      'outline_pattern_strategy_v2',
      'outline_generation_v2',
    ],
  },
  {
    id: 'content_generation_step3',
    name: 'コンテンツ生成_STEP3',
    description: 'アウトラインの品質評価とスコアリング',
    prompts: [
      'step3_scoring_qualitative_evaluation',
      'step3_scoring_summary_and_action',
    ],
  },
  {
    id: 'content_generation_step4',
    name: 'コンテンツ生成_STEP4',
    description:
      '執筆計画・エビデンス収集・本文生成・独自情報展開・ブラッシュアップ',
    prompts: [
      'step4_writing_plan_per_h2',
      'step4_intro_generation',
      'step4_section_lead_generation',
      'step4_web_info_requirements',
      'step4_rag_search_strategy',
      'step4_sonar_search',
      'step4_content_writing',
      'step4_expansion_data_understanding',
      'step4_expansion_info_strategy',
      'step4_expansion_subsection_design',
      'step4_expansion_section_lead',
      'step4_expansion_content_writing',
      'step4_refinement',
    ],
  },
  {
    id: 'agent_system',
    name: 'エージェントシステム',
    description: 'Function Calling自律エージェント用システムプロンプト',
    prompts: [
      'agent_h2_expansion_system',
      'agent_h3_unique_info_system',
      'step4_agent_h2_goal_design',
      'step4_agent_h3_goal_design',
      'agent_evaluate_progress_system',
      'agent_evaluate_progress_h2_expansion',
      'agent_evaluate_progress_h3_unique',
      'agent_sonar_search_system',
    ],
  },
  {
    id: 'fact_check',
    name: 'ファクトチェック',
    description: '事実検証・主張抽出・検証結果生成',
    prompts: [
      'fact_check_title_generation',
      'fact_check_statement_extraction',
      'fact_check_statement_optimization',
      'fact_check_verification',
    ],
  },
];

// V2変数システム（正確版）
const V2_VARIABLE_SYSTEM = {
  // 基本入力変数
  input_variables: [
    {
      name: 'keyword',
      type: 'input',
      description: 'ターゲットキーワード',
      example: 'WordPress テーマ おすすめ',
    },
    {
      name: 'search_location',
      type: 'input',
      description: '検索地域',
      example: 'Japan',
    },
    {
      name: 'search_language',
      type: 'input',
      description: '検索言語',
      example: 'ja',
    },
    {
      name: 'target_audience',
      type: 'input',
      description: 'ターゲット読者',
      example: 'ブログ初心者',
    },
    {
      name: 'content_length',
      type: 'input',
      description: '記事の長さ',
      example: '3000-5000文字',
    },
  ],

  // 外部API変数
  external_api_variables: [
    {
      name: 'dataforseo_serp',
      type: 'external',
      description: 'DataForSEO SERP結果',
      example: '{"organic": [...], "ai_overview": {...}}',
    },
    {
      name: 'dataforseo_content',
      type: 'external',
      description: 'DataForSEOコンテンツ分析',
      example: '{"headings": [...], "keywords": [...]}',
    },
    {
      name: 'dataforseo_keywords',
      type: 'external',
      description: 'DataForSEOキーワードデータ',
      example: '{"lsi_keywords": [...], "related": [...]}',
    },
    {
      name: 'sonar_pro_analysis',
      type: 'external',
      description: 'Sonar-pro HTML解析',
      example: '{"structure": {...}, "topics": [...]}',
    },
    {
      name: 'google_cse_fallback',
      type: 'external',
      description: 'Google CSEフォールバック',
      example: 'DataForSEO障害時のバックアップデータ',
    },
  ],

  // プロンプト結果変数（ステップ間受け渡し）
  step_result_variables: [
    // Step 1: テーマ設定
    {
      name: 'search_intent_analysis',
      type: 'step_result',
      description: '検索意図解析結果',
      example:
        '{"intent": "比較検討", "query_type": "commercial", "persona": {...}}',
    },
    {
      name: 'theme_cards',
      type: 'step_result',
      description: 'テーマカード一覧',
      example:
        '[{"title": "初心者向け", "angle": "簡単設定", "target": "ブログ初心者"}]',
    },
    {
      name: 'selected_theme',
      type: 'step_result',
      description: '選択されたテーマ',
      example: '{"title": "初心者向け", "angle": "簡単設定"}',
    },

    // Step 2: 競合・AIO分析
    {
      name: 'serp_summary',
      type: 'step_result',
      description: 'SERP分析サマリー',
      example: '{"volume": 12000, "difficulty": 0.7, "trend": "上昇"}',
    },
    {
      name: 'competitor_headings',
      type: 'step_result',
      description: '競合見出し構造',
      example: '{"site1": [...], "site2": [...]}',
    },
    {
      name: 'related_keywords_heatmap',
      type: 'step_result',
      description: '関連キーワードヒートマップ',
      example: '{"keywords": [...], "sites": [...], "matrix": [...]}',
    },
    {
      name: 'lsi_keywords_heatmap',
      type: 'step_result',
      description: 'LSIキーワードヒートマップ',
      example: '{"lsi": [...], "relevance": [...]}',
    },
    {
      name: 'extracted_topics',
      type: 'step_result',
      description: '抽出トピック一覧',
      example: '[{"topic": "基本知識", "strength": 5, "priority": "high"}]',
    },
    {
      name: 'selected_topics',
      type: 'step_result',
      description: '選択されたトピック',
      example: '[{"topic": "基本知識", "strength": 5}]',
    },
    {
      name: 'aio_analysis',
      type: 'step_result',
      description: 'AIO分析結果',
      example: '{"slots": [...], "requirements": [...]}',
    },
    {
      name: 'target_aio_slots',
      type: 'step_result',
      description: '狙うAIOスロット',
      example: '[{"type": "list_format", "priority": 1}]',
    },
    {
      name: 'favorite_keywords',
      type: 'step_result',
      description: 'お気に入りキーワード',
      example: '["WordPress", "テーマ", "カスタマイズ"]',
    },

    // Step 3: レシピ・トッピング選択
    {
      name: 'selected_recipe',
      type: 'step_result',
      description: '選択されたレシピ',
      example: '{"id": 123, "structure": {...}, "tone": {...}}',
    },
    {
      name: 'topping_configs',
      type: 'step_result',
      description: 'トッピング設定',
      example: '[{"id": 1, "strength": 3, "placement": "chapter_specific"}]',
    },
    {
      name: 'article_theme',
      type: 'step_result',
      description: '記事テーマ設定',
      example: '{"positioning": {...}, "unique_angle": "..."}',
    },

    // Step 4: アウトライン編集
    {
      name: 'outline_proposals',
      type: 'step_result',
      description: 'アウトライン提案3案',
      example:
        '[{"type": "A", "structure": [...]}, {"type": "B", "structure": [...]}]',
    },
    {
      name: 'selected_outline',
      type: 'step_result',
      description: '選択・編集されたアウトライン',
      example: '{"sections": [...], "metadata": {...}}',
    },
    {
      name: 'unused_headings',
      type: 'step_result',
      description: '未採用見出し',
      example: '["見出し1", "見出し2"]',
    },
    {
      name: 'latestInfoClaims',
      type: 'step_result',
      description: '最新情報・公式情報のクレームリスト',
      example:
        '[{"topic": "2025年の確定申告期限", "claim": "...", "source": "...", "reliability": "high"}]',
    },
    {
      name: 'latestInfoMeta',
      type: 'step_result',
      description: '最新情報収集のメタデータ',
      example: '{"collection_date": "2025-01-XX", "notes": "..."}',
    },

    // Step 5: コンテンツ生成
    {
      name: 'generated_intro',
      type: 'step_result',
      description: '生成されたイントロ',
      example: 'イントロ本文HTML',
    },
    {
      name: 'generated_sections',
      type: 'step_result',
      description: '生成されたセクション',
      example: '{"section1": "...", "section2": "..."}',
    },
    {
      name: 'generated_conclusion',
      type: 'step_result',
      description: '生成された結論',
      example: '結論部分HTML',
    },
    {
      name: 'evidence_mapping',
      type: 'step_result',
      description: '出典マッピング',
      example: '{"paragraph_id": ["source1", "source2"]}',
    },

    // Step 6: サマリー・エクスポート
    {
      name: 'quality_score',
      type: 'step_result',
      description: '品質スコア',
      example: '{"overall": 0.85, "seo": 0.9, "readability": 0.8}',
    },
    {
      name: 'seo_optimizations',
      type: 'step_result',
      description: 'SEO最適化結果',
      example: '{"title": "...", "meta": "...", "headings": [...]}',
    },
    {
      name: 'final_article',
      type: 'step_result',
      description: '最終記事',
      example: '{"html": "...", "markdown": "...", "json": {...}}',
    },
  ],

  // 資産変数（レシピ・トッピング）
  asset_variables: [
    {
      name: 'recipe_structure',
      type: 'asset',
      description: 'レシピ構造設定',
      example: '{"heading_patterns": [...], "section_order": [...]}',
    },
    {
      name: 'recipe_tone',
      type: 'asset',
      description: 'レシピトーン設定',
      example: '{"formality": "casual", "perspective": "second_person"}',
    },
    {
      name: 'recipe_positioning',
      type: 'asset',
      description: 'レシピポジショニング',
      example: '{"article_type": "informational", "target_audience": "..."}',
    },
    {
      name: 'topping_text',
      type: 'asset',
      description: 'テキストトッピング',
      example: '会社紹介文、独自データ等',
    },
    {
      name: 'topping_cta',
      type: 'asset',
      description: 'CTAトッピング',
      example: 'CTAテンプレート（変数対応）',
    },
    {
      name: 'topping_image',
      type: 'asset',
      description: '画像トッピング',
      example: '商品画像、インフォグラフィック等',
    },
    {
      name: 'topping_table',
      type: 'asset',
      description: 'テーブルトッピング',
      example: '比較表、料金表、仕様表等',
    },
  ],

  // 設定・コンテキスト変数
  config_context_variables: [
    {
      name: 'generation_mode',
      type: 'config',
      description: '生成モード',
      example: 'sequential_with_parallel_prep',
    },
    {
      name: 'current_section_info',
      type: 'context',
      description: '現在のセクション情報',
      example: '{"title": "メリット", "position": 3, "target_words": 800}',
    },
    {
      name: 'previous_context',
      type: 'context',
      description: '前章のコンテキスト',
      example: '前章で述べた基本知識を踏まえ...',
    },
    {
      name: 'topping_strength',
      type: 'config',
      description: 'トッピング強度',
      example: '0-4の5段階設定',
    },
    {
      name: 'evidence_requirements',
      type: 'config',
      description: '出典要件',
      example: '{"min_sources": 2, "source_types": ["official", "expert"]}',
    },
  ],
};

// プロンプトテンプレートのサンプルデータ（V2正確版）
const PROMPT_TEMPLATES = {
  // Step 1: テーマ設定
  analyze_search_intent: {
    id: 'analyze_search_intent',
    name: '検索意図解析',
    category: 'テーマ設定',
    currentVersion: 1,
    memo: 'Sonar-proデータを活用してキーワードの検索意図を詳細分析',
    versions: [
      {
        version: 1,
        model: 'gpt-4.1',
        template: `キーワード「{keyword}」の検索意図を詳細分析してください。

DataForSEO SERPデータ: {dataforseo_serp}
DataForSEOキーワードデータ: {dataforseo_keywords}
Sonar-pro分析結果: {sonar_pro_analysis}

以下の形式で分析結果をJSON出力してください：
{
  "intent": "情報収集/比較検討/購買意図/ナビゲーション",
  "query_type": "informational/commercial/transactional/navigational", 
  "search_volume": "月間検索ボリューム",
  "difficulty": "SEO難易度（0-1）",
  "trend": "上昇/安定/下降",
  "persona": {
    "primary_target": "メインターゲット",
    "demographics": "年代・属性",
    "pain_points": ["課題1", "課題2"],
    "goals": ["目標1", "目標2"]
  },
  "related_intents": ["関連する検索意図"],
  "content_expectations": "期待されるコンテンツの特徴"
}`,
        variables: [
          'keyword',
          'dataforseo_serp',
          'dataforseo_keywords',
          'sonar_pro_analysis',
        ],
        isActive: true,
        createdAt: '2025-01-19T10:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  generate_theme_cards: {
    id: 'generate_theme_cards',
    name: 'テーマカード生成',
    category: 'テーマ設定',
    currentVersion: 1,
    memo: '検索意図分析結果から3-5個のテーマカードを生成',
    versions: [
      {
        version: 1,
        model: 'claude-3-sonnet',
        template: `検索意図分析結果を基に、3-5個のテーマカードを生成してください。

検索意図分析結果: {search_intent_analysis}
ターゲット読者: {target_audience}

各テーマカードには以下を含めてください：
- title: テーマタイトル
- angle: 記事の切り口・アプローチ
- target: 具体的なターゲット読者
- unique_value: 独自価値・差別化ポイント
- content_focus: コンテンツの重点領域

JSON配列形式で出力してください：
[
  {
    "title": "テーマタイトル",
    "angle": "記事の切り口",
    "target": "ターゲット読者",
    "unique_value": "独自価値",
    "content_focus": ["重点1", "重点2", "重点3"]
  }
]`,
        variables: ['search_intent_analysis', 'target_audience'],
        isActive: true,
        createdAt: '2025-01-19T11:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  // Step 2: 競合・AIO分析
  serp_analysis: {
    id: 'serp_analysis',
    name: 'SERP分析',
    category: '競合・AIO分析',
    currentVersion: 1,
    memo: 'DataForSEO SERPデータの包括的分析',
    versions: [
      {
        version: 1,
        model: 'gpt-4.1',
        template: `DataForSEO SERPデータを分析し、競合状況とSERP特徴を把握してください。

SERPデータ: {dataforseo_serp}
検索キーワード: {keyword}
検索地域: {search_location}

以下の観点で分析してください：
1. 検索結果の特徴（ニュース、画像、動画、ショッピング等）
2. 上位10サイトの傾向分析
3. AI Overview（AIO）の有無と内容
4. その他のSERP機能（PAA、ローカルパック等）
5. 競合の強さとSEO難易度

JSON形式で出力：
{
  "serp_features": ["feature1", "feature2"],
  "top_sites_analysis": {
    "domain_authority_range": "範囲",
    "content_types": ["タイプ1", "タイプ2"],
    "common_patterns": "共通パターン"
  },
  "ai_overview": {
    "present": true/false,
    "content_summary": "AIO内容の要約",
    "sources_count": "ソース数"
  },
  "difficulty_assessment": {
    "overall": "評価",
    "factors": ["要因1", "要因2"]
  }
}`,
        variables: ['dataforseo_serp', 'keyword', 'search_location'],
        isActive: true,
        createdAt: '2025-01-19T12:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  competitor_content_analysis: {
    id: 'competitor_content_analysis',
    name: '競合コンテンツ分析',
    category: '競合・AIO分析',
    currentVersion: 1,
    memo: 'DataForSEOコンテンツ分析による競合サイトの詳細解析',
    versions: [
      {
        version: 1,
        model: 'claude-3-sonnet',
        template: `競合サイトのコンテンツを詳細分析してください。

DataForSEOコンテンツ分析: {dataforseo_content}
Sonar-pro分析: {sonar_pro_analysis}

分析項目：
1. 見出し構造の分析（H1-H6の使用パターン）
2. キーワード使用頻度と配置
3. コンテンツの長さと構成
4. 内部リンク戦略
5. 画像・メディアの使用状況

競合3.5列スライダー表示用のデータ構造で出力：
{
  "heading_analysis": {
    "site1": {"h1": "...", "h2": ["...", "..."], "h3": [...]},
    "site2": {"h1": "...", "h2": ["...", "..."], "h3": [...]},
    "patterns": "共通パターンの分析"
  },
  "keyword_heatmap": {
    "keywords": ["キーワード1", "キーワード2"],
    "sites": ["site1", "site2", "site3"],
    "frequency_matrix": [[頻度データ]]
  },
  "content_insights": {
    "avg_length": "平均文字数",
    "common_sections": ["共通セクション"],
    "differentiation_opportunities": ["差別化機会"]
  }
}`,
        variables: ['dataforseo_content', 'sonar_pro_analysis'],
        isActive: true,
        createdAt: '2025-01-19T13:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  aio_slot_extraction: {
    id: 'aio_slot_extraction',
    name: 'AIOスロット抽出',
    category: '競合・AIO分析',
    currentVersion: 1,
    memo: 'AI Overviewの構成を解剖してスロットタイプを特定',
    versions: [
      {
        version: 1,
        model: 'gpt-4.1',
        template: `AI Overview（AIO）の構成を詳細分析し、スロットタイプを抽出してください。

AIOデータ: {aio_analysis}
SERPデータ: {dataforseo_serp}

スロットタイプの分類：
- list_format: 箇条書き形式
- table_format: 表形式  
- paragraph_format: 段落形式
- qa_format: Q&A形式
- step_format: ステップ形式

各スロットの要件を分析：
{
  "aio_structure": {
    "total_slots": "スロット数",
    "slot_breakdown": [
      {
        "type": "list_format",
        "position": 1,
        "requirements": "要件説明",
        "content_length": "文字数目安",
        "priority": "high/medium/low"
      }
    ]
  },
  "targeting_strategy": {
    "recommended_slots": ["狙うべきスロット"],
    "difficulty": "獲得難易度",
    "content_requirements": "必要なコンテンツ要素"
  },
  "optimization_tips": ["最適化のヒント"]
}`,
        variables: ['aio_analysis', 'dataforseo_serp'],
        isActive: true,
        createdAt: '2025-01-19T14:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  topic_extraction: {
    id: 'topic_extraction',
    name: 'トピック抽出',
    category: '競合・AIO分析',
    currentVersion: 1,
    memo: '競合分析からトピック一覧を抽出し、強度設定を提案',
    versions: [
      {
        version: 1,
        model: 'claude-3-sonnet',
        template: `競合分析結果からトピック一覧を抽出し、各トピックの重要度と強度を設定してください。

競合見出し構造: {competitor_headings}
関連キーワードヒートマップ: {related_keywords_heatmap}
LSIキーワードヒートマップ: {lsi_keywords_heatmap}
選択されたテーマ: {selected_theme}

トピック抽出基準：
1. 競合サイトで頻出するトピック
2. 検索意図に合致するトピック  
3. 差別化可能なトピック
4. AIO要件に必要なトピック

JSON形式で出力：
{
  "extracted_topics": [
    {
      "topic": "トピック名",
      "category": "high/medium/low",
      "strength_recommendation": 1-5,
      "rationale": "選定理由",
      "competitor_coverage": "競合での扱われ方",
      "differentiation_angle": "差別化の角度"
    }
  ],
  "topic_relationships": {
    "core_topics": ["必須トピック"],
    "supporting_topics": ["補助トピック"],
    "optional_topics": ["オプショナルトピック"]
  }
}`,
        variables: [
          'competitor_headings',
          'related_keywords_heatmap',
          'lsi_keywords_heatmap',
          'selected_theme',
        ],
        isActive: true,
        createdAt: '2025-01-19T15:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  // Step 3: レシピ・トッピング選択
  recipe_recommendation: {
    id: 'recipe_recommendation',
    name: 'レシピ推奨',
    category: 'レシピ・トッピング選択',
    currentVersion: 1,
    memo: '分析結果に基づいて最適なレシピを推奨',
    versions: [
      {
        version: 1,
        model: 'gpt-4.1',
        template: `分析結果を基に最適なレシピ（記事構成パターン・文体）を推奨してください。

検索意図分析: {search_intent_analysis}
選択されたテーマ: {selected_theme}
抽出トピック: {extracted_topics}
ターゲット読者: {target_audience}

推奨するレシピの要素：
1. 記事構造（heading_patterns, section_order, depth_rules）
2. 文体設定（formality, perspective, voice, sentence_complexity）
3. ポジショニング（article_type, unique_angle）

JSON形式で出力：
{
  "recommended_recipe": {
    "structure": {
      "heading_patterns": "見出しパターン",
      "section_order": ["セクション順序"],
      "depth_rules": "階層ルール"
    },
    "tone": {
      "formality": "casual/neutral/formal/professional",
      "perspective": "first_person/second_person/third_person", 
      "voice": "active/passive",
      "sentence_complexity": "simple/moderate/complex"
    },
    "positioning": {
      "article_type": "informational/commercial/transactional",
      "target_audience": "ペルソナ定義",
      "unique_angle": "差別化ポイント"
    }
  },
  "rationale": "推奨理由",
  "alternatives": ["代替案"]
}`,
        variables: [
          'search_intent_analysis',
          'selected_theme',
          'extracted_topics',
          'target_audience',
        ],
        isActive: true,
        createdAt: '2025-01-19T16:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  // 各サイト概要生成: 競合サイトの基本情報から概要を生成
  site_summary_generation: {
    id: 'site_summary_generation',
    name: 'サイト概要生成',
    category: 'SEO/AIO分析',
    currentVersion: 1,
    memo: '競合サイトの基本情報から概要を生成',
    versions: [
      {
        version: 1,
        model: 'gpt-4.1',
        template: `キーワード「{keyword}」のサイト概要を生成してください。

サイトURL: {site_url}
サイトタイトル: {site_title}

以下の形式で概要を生成してください：
- 一般的なブログの収益化方法
- 平均的なブログ収入の範囲
- ブログを始める際の注意点

JSON形式で出力：
{
  "summary": "サイト概要",
  "keywords": ["ブログ", "収益化", "平均収入", "始め方"],
  "tone": "正式",
  "length": "150-200文字"
}`,
        variables: ['keyword', 'site_url', 'site_title'],
        isActive: true,
        createdAt: '2025-01-19T17:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },

  // 検索意図の深掘り分析: 検索意図を3つの要素で構造化分析
  search_intent_deepdive: {
    id: 'search_intent_deepdive',
    name: '検索意図の深掘り分析',
    category: 'SEO/AIO分析',
    currentVersion: 1,
    memo: '検索意図を3つの要素で構造化分析',
    versions: [
      {
        version: 1,
        model: 'gpt-4.1',
        template: `キーワード「{keyword}」の検索意図を3つの要素で構造化分析してください。

分析対象の検索キーワード: {keyword}
分析実行日時: {analysis_timestamp}
上位サイトタイトル一覧: {competitor_titles}
統合されたトピック一覧: {unified_topics}
People Also Ask情報: {people_also_ask}
SERP特徴情報: {serp_features}

以下の形式で分析結果をJSON出力してください：
{
  "analysis_result": {
    "keyword": "ブログ 収益化",
    "analysis_timestamp": "2025-07-30T12:00:00Z",
    "competitor_titles": ["タイトル1", "タイトル2", "タイトル3"],
    "unified_topics": ["収益化方法", "平均収入", "始め方"],
    "people_also_ask": ["関連質問1", "関連質問2"],
    "serp_features": {"ai_overview": true, "featured_snippet": false}
  }
}`,
        variables: [
          'keyword',
          'analysis_timestamp',
          'competitor_titles',
          'unified_topics',
          'people_also_ask',
          'serp_features',
        ],
        isActive: true,
        createdAt: '2025-01-19T18:00:00',
        createdBy: 'admin',
        changeNote: 'V2フロー対応版',
      },
    ],
    lastExecutions: [],
  },
};

// 検索キーワード・LSI分析の使用可能変数定義
const V2_AVAILABLE_VARIABLES = {
  // 基本入力変数
  input_variables: [
    {
      name: 'keyword',
      type: 'input',
      description: 'ターゲットキーワード',
      example: 'WordPress テーマ おすすめ',
    },
    {
      name: 'search_location',
      type: 'input',
      description: '検索地域',
      example: 'Japan',
    },
    {
      name: 'search_language',
      type: 'input',
      description: '検索言語',
      example: 'ja',
    },
    {
      name: 'target_audience',
      type: 'input',
      description: 'ターゲット読者',
      example: 'ブログ初心者',
    },
    {
      name: 'content_length',
      type: 'input',
      description: '記事の長さ',
      example: '3000-5000文字',
    },
  ],

  // 外部API変数
  external_api_variables: [
    {
      name: 'dataforseo_serp',
      type: 'external',
      description: 'DataForSEO SERP結果',
      example: '{"organic": [...], "ai_overview": {...}}',
    },
    {
      name: 'dataforseo_content',
      type: 'external',
      description: 'DataForSEOコンテンツ分析',
      example: '{"headings": [...], "keywords": [...]}',
    },
    {
      name: 'dataforseo_keywords',
      type: 'external',
      description: 'DataForSEOキーワードデータ',
      example: '{"lsi_keywords": [...], "related": [...]}',
    },
    {
      name: 'sonar_pro_analysis',
      type: 'external',
      description: 'Sonar-pro HTML解析',
      example: '{"structure": {...}, "topics": [...]}',
    },
    {
      name: 'google_cse_fallback',
      type: 'external',
      description: 'Google CSEフォールバック',
      example: 'DataForSEO障害時のバックアップデータ',
    },
  ],

  // プロンプト結果変数（ステップ間受け渡し）
  step_result_variables: [
    // Step 1: テーマ設定
    {
      name: 'search_intent_analysis',
      type: 'step_result',
      description: '検索意図解析結果',
      example:
        '{"intent": "比較検討", "query_type": "commercial", "persona": {...}}',
    },
    {
      name: 'theme_cards',
      type: 'step_result',
      description: 'テーマカード一覧',
      example:
        '[{"title": "初心者向け", "angle": "簡単設定", "target": "ブログ初心者"}]',
    },
    {
      name: 'selected_theme',
      type: 'step_result',
      description: '選択されたテーマ',
      example: '{"title": "初心者向け", "angle": "簡単設定"}',
    },

    // Step 2: 競合・AIO分析
    {
      name: 'serp_summary',
      type: 'step_result',
      description: 'SERP分析サマリー',
      example: '{"volume": 12000, "difficulty": 0.7, "trend": "上昇"}',
    },
    {
      name: 'competitor_headings',
      type: 'step_result',
      description: '競合見出し構造',
      example: '{"site1": [...], "site2": [...]}',
    },
    {
      name: 'related_keywords_heatmap',
      type: 'step_result',
      description: '関連キーワードヒートマップ',
      example: '{"keywords": [...], "sites": [...], "matrix": [...]}',
    },
    {
      name: 'lsi_keywords_heatmap',
      type: 'step_result',
      description: 'LSIキーワードヒートマップ',
      example: '{"lsi": [...], "relevance": [...]}',
    },
    {
      name: 'extracted_topics',
      type: 'step_result',
      description: '抽出トピック一覧',
      example: '[{"topic": "基本知識", "strength": 5, "priority": "high"}]',
    },
    {
      name: 'selected_topics',
      type: 'step_result',
      description: '選択されたトピック',
      example: '[{"topic": "基本知識", "strength": 5}]',
    },
    {
      name: 'aio_analysis',
      type: 'step_result',
      description: 'AIO分析結果',
      example: '{"slots": [...], "requirements": [...]}',
    },
    {
      name: 'target_aio_slots',
      type: 'step_result',
      description: '狙うAIOスロット',
      example: '[{"type": "list_format", "priority": 1}]',
    },
    {
      name: 'favorite_keywords',
      type: 'step_result',
      description: 'お気に入りキーワード',
      example: '["WordPress", "テーマ", "カスタマイズ"]',
    },

    // Step 3: レシピ・トッピング選択
    {
      name: 'selected_recipe',
      type: 'step_result',
      description: '選択されたレシピ',
      example: '{"id": 123, "structure": {...}, "tone": {...}}',
    },
    {
      name: 'topping_configs',
      type: 'step_result',
      description: 'トッピング設定',
      example: '[{"id": 1, "strength": 3, "placement": "chapter_specific"}]',
    },
    {
      name: 'article_theme',
      type: 'step_result',
      description: '記事テーマ設定',
      example: '{"positioning": {...}, "unique_angle": "..."}',
    },

    // Step 4: アウトライン編集
    {
      name: 'outline_proposals',
      type: 'step_result',
      description: 'アウトライン提案3案',
      example:
        '[{"type": "A", "structure": [...]}, {"type": "B", "structure": [...]}]',
    },
    {
      name: 'selected_outline',
      type: 'step_result',
      description: '選択・編集されたアウトライン',
      example: '{"sections": [...], "metadata": {...}}',
    },
    {
      name: 'unused_headings',
      type: 'step_result',
      description: '未採用見出し',
      example: '["見出し1", "見出し2"]',
    },
    {
      name: 'latestInfoClaims',
      type: 'step_result',
      description: '最新情報・公式情報のクレームリスト',
      example:
        '[{"topic": "2025年の確定申告期限", "claim": "...", "source": "...", "reliability": "high"}]',
    },
    {
      name: 'latestInfoMeta',
      type: 'step_result',
      description: '最新情報収集のメタデータ',
      example: '{"collection_date": "2025-01-XX", "notes": "..."}',
    },

    // Step 5: コンテンツ生成
    {
      name: 'generated_intro',
      type: 'step_result',
      description: '生成されたイントロ',
      example: 'イントロ本文HTML',
    },
    {
      name: 'generated_sections',
      type: 'step_result',
      description: '生成されたセクション',
      example: '{"section1": "...", "section2": "..."}',
    },
    {
      name: 'generated_conclusion',
      type: 'step_result',
      description: '生成された結論',
      example: '結論部分HTML',
    },
    {
      name: 'evidence_mapping',
      type: 'step_result',
      description: '出典マッピング',
      example: '{"paragraph_id": ["source1", "source2"]}',
    },

    // Step 6: サマリー・エクスポート
    {
      name: 'quality_score',
      type: 'step_result',
      description: '品質スコア',
      example: '{"overall": 0.85, "seo": 0.9, "readability": 0.8}',
    },
    {
      name: 'seo_optimizations',
      type: 'step_result',
      description: 'SEO最適化結果',
      example: '{"title": "...", "meta": "...", "headings": [...]}',
    },
    {
      name: 'final_article',
      type: 'step_result',
      description: '最終記事',
      example: '{"html": "...", "markdown": "...", "json": {...}}',
    },
  ],

  // 資産変数（レシピ・トッピング）
  asset_variables: [
    {
      name: 'recipe_structure',
      type: 'asset',
      description: 'レシピ構造設定',
      example: '{"heading_patterns": [...], "section_order": [...]}',
    },
    {
      name: 'recipe_tone',
      type: 'asset',
      description: 'レシピトーン設定',
      example: '{"formality": "casual", "perspective": "second_person"}',
    },
    {
      name: 'recipe_positioning',
      type: 'asset',
      description: 'レシピポジショニング',
      example: '{"article_type": "informational", "target_audience": "..."}',
    },
    {
      name: 'topping_text',
      type: 'asset',
      description: 'テキストトッピング',
      example: '会社紹介文、独自データ等',
    },
    {
      name: 'topping_cta',
      type: 'asset',
      description: 'CTAトッピング',
      example: 'CTAテンプレート（変数対応）',
    },
    {
      name: 'topping_image',
      type: 'asset',
      description: '画像トッピング',
      example: '商品画像、インフォグラフィック等',
    },
    {
      name: 'topping_table',
      type: 'asset',
      description: 'テーブルトッピング',
      example: '比較表、料金表、仕様表等',
    },
  ],

  // 設定・コンテキスト変数
  config_context_variables: [
    {
      name: 'generation_mode',
      type: 'config',
      description: '生成モード',
      example: 'sequential_with_parallel_prep',
    },
    {
      name: 'current_section_info',
      type: 'context',
      description: '現在のセクション情報',
      example: '{"title": "メリット", "position": 3, "target_words": 800}',
    },
    {
      name: 'previous_context',
      type: 'context',
      description: '前章のコンテキスト',
      example: '前章で述べた基本知識を踏まえ...',
    },
    {
      name: 'topping_strength',
      type: 'config',
      description: 'トッピング強度',
      example: '0-4の5段階設定',
    },
    {
      name: 'evidence_requirements',
      type: 'config',
      description: '出典要件',
      example: '{"min_sources": 2, "source_types": ["official", "expert"]}',
    },
  ],
};

const PromptManagement = () => {
  // 基本状態
  const [hasPermission, setHasPermission] = useState(false);
  const [checkingPermission, setCheckingPermission] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [selectedStep, setSelectedStep] = useState('seo_aio_analysis'); // カテゴリーIDとして継続利用
  const [selectedPrompt, setSelectedPrompt] = useState(null);

  // 統合モーダル状態
  const [modalVisible, setModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('edit');

  // 変数プレビュー用
  const [variableValues, setVariableValues] = useState({});
  const [previewContent, setPreviewContent] = useState('');

  // テスト実行用
  const [testVariables, setTestVariables] = useState({});
  const [testResult, setTestResult] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  // モデルごとの thinking / effort 対応表（バックエンドが正典）
  const [modelCapabilities, setModelCapabilities] = useState({
    thinking_choices: [],
    models: {},
  });
  const [testExecutionId, setTestExecutionId] = useState('');

  // DB分析データ関連のstate
  const [useRealData, setUseRealData] = useState(false);
  const [analysisDataList, setAnalysisDataList] = useState([]);
  const [selectedAnalysisId, setSelectedAnalysisId] = useState(null);
  const [loadingAnalysisData, setLoadingAnalysisData] = useState(false);

  // スタイル選択用のstate
  const [styleList, setStyleList] = useState([]);
  const [selectedStyleId, setSelectedStyleId] = useState(null);
  const [loadingStyles, setLoadingStyles] = useState(false);

  // フォーム
  const [editForm] = Form.useForm();
  const [versionForm] = Form.useForm();

  // テキストエリアのref
  const templateRef = useRef(null);

  // プロンプトプレビュー機能を追加
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewVersion, setPreviewVersion] = useState(null);

  const handlePreviewVersion = version => {
    setPreviewVersion(version);
    setPreviewVisible(true);
  };

  const closePreview = () => {
    setPreviewVisible(false);
    setPreviewVersion(null);
  };

  // 権限チェックと初期データ読み込み
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        const isAuth = isAuthenticated();
        if (!isAuth) {
          setHasPermission(false);
          setCheckingPermission(false);
          return;
        }

        const userRole = await getUserRole();
        const hasMasterPermission = userRole === 'master';
        setHasPermission(hasMasterPermission);

        if (hasMasterPermission) {
          await loadPrompts();
          await loadAnalysisData(); // 分析データ読み込みを追加
        }
      } catch (error) {
        console.error('Permission check failed:', error);
        setHasPermission(false);
      } finally {
        setCheckingPermission(false);
      }
    };

    checkPermissions();
  }, []);

  // 初期データ投入機能は撤去（ユーザー要望）
  const handleInitializePrompts = async () => {
    message.warning('初期データ投入機能は廃止されました');
  };

  // プレビュー更新
  useEffect(() => {
    if (selectedPrompt && activeTab === 'preview') {
      updatePreview();
    }
  }, [selectedPrompt, variableValues, activeTab]);

  // テンプレート変更時のプレビュー更新
  useEffect(() => {
    if (activeTab === 'preview') {
      const template = editForm.getFieldValue('template');
      if (template) {
        const preview = replaceVariables(template, variableValues);
        setPreviewContent(preview);
      }
    }
  }, [editForm.getFieldValue('template'), variableValues, activeTab]);

  // 実データ使用チェック時の分析データ読み込み
  useEffect(() => {
    if (useRealData && hasPermission && analysisDataList.length === 0) {
      loadAnalysisData();
    }
  }, [useRealData, hasPermission]);

  // モデル対応表の取得（thinking / effort の出し分けに使う）
  useEffect(() => {
    if (!hasPermission) return;
    let cancelled = false;
    v2PromptsApi.getModelCapabilities().then(caps => {
      if (!cancelled) setModelCapabilities(caps);
    });
    return () => {
      cancelled = true;
    };
  }, [hasPermission]);

  // プロンプト行クリック
  const handleRowClick = prompt => {
    setSelectedPrompt(prompt);
    // アクティブバージョンを取得（is_active=trueのバージョン）
    const currentVersion = prompt.versions?.find(v => v.is_active === true);

    if (!currentVersion) {
      message.error('現在のバージョンが見つかりません');
      return;
    }

    // フォーム初期化
    editForm.setFieldsValue({
      name: prompt.name,
      memo: prompt.memo,
      // 既定値は必ずプルダウンに存在する現行モデルにする
      // （gpt-4-turbo は選択肢に無く 2026/10/23 提供終了予定だった）
      model: currentVersion.model || 'gpt-4.1',
      template: currentVersion.template || '',
      isActive: currentVersion.isActive !== false,
      // 保存済みの thinking / effort（未設定は「モデル既定に任せる」）
      thinkingMode: currentVersion.model_options?.thinking || 'default',
      effort: currentVersion.model_options?.effort || undefined,
    });

    // 変数初期化
    const initialValues = {};
    if (
      currentVersion.variables &&
      typeof currentVersion.variables === 'object'
    ) {
      Object.keys(currentVersion.variables).forEach(variable => {
        initialValues[variable] = `{${variable}}`;
      });
    }
    setVariableValues(initialValues);

    // テスト実行状態をリセット
    setTestVariables({});
    setTestResult('');
    setTestExecutionId('');

    setActiveTab('edit');
    setModalVisible(true);
  };

  // プレビュー更新
  const updatePreview = () => {
    if (!selectedPrompt) return;

    const currentVersion = selectedPrompt.versions?.find(
      v => v.version === selectedPrompt.current_version,
    );
    if (!currentVersion || !currentVersion.template) return;

    let preview = currentVersion.template;

    // プロンプトIDに対応する変数のみを使用
    const availableVariables = getAvailableVariables(selectedPrompt.prompt_id);
    const relevantVariables = availableVariables.map(v => v.name);

    // 該当する変数のみを置換
    relevantVariables.forEach(variable => {
      const value = variableValues[variable];
      if (value && value.trim()) {
        preview = preview.replace(new RegExp(`\\{${variable}\\}`, 'g'), value);
      }
    });

    setPreviewContent(preview);
  };

  // 変数置換のヘルパー関数
  const replaceVariables = (template, values) => {
    if (!template) return '';

    let result = template;
    Object.entries(values).forEach(([key, value]) => {
      if (value && value.trim()) {
        result = result.replace(new RegExp(`\\{${key}\\}`, 'g'), value);
      }
    });

    return result;
  };

  // 変数値変更
  const handleVariableChange = (variable, value) => {
    setVariableValues(prev => ({
      ...prev,
      [variable]: value,
    }));
  };

  // プロンプト詳細を再取得する関数
  const reloadSelectedPrompt = async promptId => {
    try {
      await loadPrompts(); // 全体のリストを更新
      const updatedPrompts = await v2PromptsApi.getPrompts();
      const updatedPrompt = updatedPrompts.find(p => p.prompt_id === promptId);
      if (updatedPrompt) {
        setSelectedPrompt(updatedPrompt);
      }
    } catch (error) {
      console.error('プロンプト詳細の再取得エラー:', error);
    }
  };

  // プロンプト保存（現在のアクティブバージョンを更新）
  // フォーム値から保存用の model_options を組み立てる。
  // 「モデル既定に任せる」は値を持たせない（空 = 既定 という意味を保つため）。
  const buildModelOptions = values => {
    const options = {};
    if (values.thinkingMode && values.thinkingMode !== 'default') {
      options.thinking = values.thinkingMode;
    }
    if (values.effort) {
      options.effort = values.effort;
    }
    return options;
  };

  const handleSavePrompt = async values => {
    try {
      // 変数を抽出
      const variables = {};
      extractVariables(values.template).forEach(varName => {
        variables[varName] = {
          type: 'input',
          description: `変数: ${varName}`,
          required: true,
        };
      });

      // プロンプトとアクティブバージョンを同時に更新（create_new_version: false）
      await v2PromptsApi.updatePrompt(selectedPrompt.prompt_id, {
        name: values.name,
        memo: values.memo,
        is_active: values.isActive,
        template: values.template,
        model: values.model,
        variables: variables,
        model_options: buildModelOptions(values),
        change_note: '編集タブからの更新',
        create_new_version: false, // 新バージョンを作成せず、現在のバージョンを更新
      });

      message.success('現在のバージョンを更新しました');
      await reloadSelectedPrompt(selectedPrompt.prompt_id); // バージョンリストを更新
      setModalVisible(false);
    } catch (error) {
      console.error('保存エラー:', error);
      message.error('保存に失敗しました');
    }
  };

  // プロンプト一覧の読み込み
  const loadPrompts = async () => {
    try {
      const data = await v2PromptsApi.getPrompts();

      // デバッグ：APIレスポンスをコンソールに出力
      console.log('🔍 API Response:', data);
      const firstPromptWithExecution = data?.find(
        p => p.last_executions && p.last_executions.length > 0,
      );
      console.log('🔍 First prompt with executions:', firstPromptWithExecution);
      if (
        firstPromptWithExecution &&
        firstPromptWithExecution.last_executions.length > 0
      ) {
        const firstExec = firstPromptWithExecution.last_executions[0];
        console.log('🔍 First execution sample:', firstExec);
        console.log('🔍 All fields in execution:', Object.keys(firstExec));
        console.log('🔍 executed_at value:', firstExec.executed_at);
        console.log('🔍 executed_at type:', typeof firstExec.executed_at);
        console.log('🔍 created_at value:', firstExec.created_at);
        console.log('🔍 created_at type:', typeof firstExec.created_at);
        console.log('🔍 id value:', firstExec.id);
        console.log('🔍 version value:', firstExec.version);
      }

      // 生成フローの処理順でプロンプトを並び替え
      const sortedPrompts = sortPromptsByGenerationFlow(data || []);
      setPrompts(sortedPrompts);

      // デバッグ：ソート後のプロンプト情報
      console.log('🔍 Sorted prompts:', sortedPrompts);
    } catch (error) {
      console.error('プロンプト取得エラー:', error);
      message.error('プロンプトの取得に失敗しました');
    }
  };

  // 分析データ一覧の読み込み（生成結果一覧と同じデータソース使用）
  const loadAnalysisData = async () => {
    try {
      setLoadingAnalysisData(true);
      const data = await v2Step1AnalysisApi.getUserAnalyses();

      console.log('🔍 Analysis Data Response (from user-analyses):', data);

      if (data && data.analyses) {
        // analysis_cacheのデータを分析データ選択肢に変換
        const analysisOptions = data.analyses.map(analysis => ({
          analysis_id: analysis.analysis_id,
          keyword: analysis.keyword,
          created_at: analysis.timestamp, // timestampを使用
          progress: analysis.progress,
          status: analysis.status,
          current_step: analysis.current_step,
          service_type: analysis.service_type,
        }));

        setAnalysisDataList(analysisOptions);
        console.log(
          `✅ Loaded ${analysisOptions.length} analysis data items from cache`,
        );
      } else {
        setAnalysisDataList([]);
        console.log('⚠️ No analysis data found in cache');
      }
    } catch (error) {
      console.error('分析データ取得エラー:', error);
      setAnalysisDataList([]);
      // エラーは表示しない（初回は空の場合が多いため）
    } finally {
      setLoadingAnalysisData(false);
    }
  };

  // 生成フローの処理順でプロンプトを並び替える関数
  const sortPromptsByGenerationFlow = prompts => {
    // 全てのプロンプトIDを生成フローの順番で並べる
    const flowOrder = [];
    GENERATION_FLOW.forEach(step => {
      step.prompts.forEach(promptId => {
        if (!flowOrder.includes(promptId)) {
          flowOrder.push(promptId);
        }
      });
    });

    // フローに含まれるプロンプトとそうでないプロンプトに分ける
    const flowPrompts = [];
    const otherPrompts = [];

    prompts.forEach(prompt => {
      if (flowOrder.includes(prompt.prompt_id)) {
        flowPrompts.push(prompt);
      } else {
        otherPrompts.push(prompt);
      }
    });

    // フローに含まれるプロンプトを定義順に並び替え
    flowPrompts.sort((a, b) => {
      const indexA = flowOrder.indexOf(a.prompt_id);
      const indexB = flowOrder.indexOf(b.prompt_id);
      return indexA - indexB;
    });

    // フローに含まれるプロンプトを先頭に、その他のプロンプトを後に配置
    return [...flowPrompts, ...otherPrompts];
  };

  // 新バージョン作成（編集タブから）
  const handleCreateNewVersion = async () => {
    const currentTemplate = editForm.getFieldValue('template');
    if (!currentTemplate || !currentTemplate.trim()) {
      message.error('テンプレートが空です');
      return;
    }

    versionForm.setFieldsValue({
      changeNote: '',
    });

    // 新バージョン作成フォームを表示（この場合は直接作成）
    Modal.confirm({
      title: '新バージョン作成',
      content: '現在のテンプレートで新バージョンを作成しますか？',
      onOk: async () => {
        await handleCreateVersionFromTab({
          changeNote: '編集タブからの新バージョン作成',
        });
      },
    });
  };

  const handleCreateVersionFromTab = async values => {
    try {
      const currentTemplate = editForm.getFieldValue('template');
      const currentModel = editForm.getFieldValue('model');

      const variables = {};
      extractVariables(currentTemplate).forEach(varName => {
        variables[varName] = {
          type: 'input',
          description: `変数: ${varName}`,
          required: true,
        };
      });

      await v2PromptsApi.createVersion(selectedPrompt.prompt_id, {
        template: currentTemplate,
        model: currentModel,
        variables: variables,
        change_note: values.changeNote,
        is_active: true,
      });

      message.success('新バージョンを作成しました');
      await reloadSelectedPrompt(selectedPrompt.prompt_id); // バージョンリストを更新
      versionForm.resetFields();
    } catch (error) {
      console.error('バージョン作成エラー:', error);
      message.error('バージョンの作成に失敗しました');
    }
  };

  const handleSwitchVersion = async version => {
    try {
      // 新しいバージョン切り替えAPIを使用
      await v2PromptsApi.activateVersion(
        selectedPrompt.prompt_id,
        version.version,
      );

      message.success(`バージョンv${version.version}に切り替えました`);
      await reloadSelectedPrompt(selectedPrompt.prompt_id);
    } catch (error) {
      console.error('バージョン切り替えエラー:', error);
      message.error('バージョンの切り替えに失敗しました');
    }
  };

  // 選択されたステップのプロンプト取得
  const getPromptsForStep = stepId => {
    const step = GENERATION_FLOW.find(s => s.id === stepId);
    const promptIds = step ? step.prompts : [];
    return prompts.filter(prompt => promptIds.includes(prompt.prompt_id));
  };

  // プロンプトで使用可能な変数を取得
  const getAvailableVariables = promptId => {
    // プロンプトIDに基づいて適切な変数を返す（実際の仕様に基づく）
    const stepMapping = {
      // 生トピック抽出: キーワード + 上位10サイトの見出しデータ
      'topic_extraction_raw': [
        {
          name: 'keyword',
          type: 'input',
          description: 'ターゲットキーワード（ユーザーが入力したキーワード）',
          example: 'WordPress テーマ おすすめ',
        },
        {
          name: 'competitor_headings_data',
          type: 'external',
          description: '上位10サイトすべての見出しデータ',
          example: '{"competitor_details": [...]}',
        },
      ],
      // トピック統合/MECE整理: 生トピック抽出の出力結果
      'topic_unification_mece': [
        {
          name: 'keyword',
          type: 'input',
          description: 'ターゲットキーワード',
          example: 'WordPress テーマ おすすめ',
        },
        {
          name: 'raw_topics_data',
          type: 'step_result',
          description: '生トピック抽出の出力結果',
          example:
            '{"keyword": "...", "sites_analysis": [...], "extraction_summary": {...}}',
        },
      ],
      // 各サイト別トピック分析: 統合トピック + 各サイトの見出し構造
      'site_topic_analysis': [
        {
          name: 'site_url',
          type: 'input',
          description: '分析対象サイトのURL',
          example: 'https://example.com/article',
        },
        {
          name: 'site_title',
          type: 'input',
          description: '分析対象サイトのタイトル',
          example: 'WordPressテーマ比較記事',
        },
        {
          name: 'unified_topics',
          type: 'step_result',
          description: '統合されたトピック一覧（JSON配列）',
          example:
            '["SEO基本知識", "WordPressテーマ選び方", "カスタマイズ方法"]',
        },
        {
          name: 'site_headings',
          type: 'external',
          description: '対象サイトの見出し構造（H1-H4の階層構造）',
          example:
            '{"headings": [{"level": 1, "text": "見出し1"}, {"level": 2, "text": "見出し2"}]}',
        },
      ],
      // スタイル解析（YAML抽出用の厳密JSONを出力）
      'style_analysis_yaml_v1': [
        {
          name: 'article_text',
          type: 'input',
          description: '解析対象の記事本文（改行含む）',
          example: '本文全文をここに貼り付け',
        },
        {
          name: 'pre_features_json',
          type: 'input',
          description: '事前抽出した補助特徴量（JSON文字列）。未使用なら空文字',
          example: '{"avg_sentence_len": 42, "has_table": true}',
        },
        {
          name: 'language',
          type: 'input',
          description: '解析対象の言語コード',
          example: 'ja',
        },
      ],
      // 重要語句正規化: 検索キーワード + 抽出された重要語句
      'terms_normalization': [
        {
          name: 'keyword',
          type: 'input',
          description: 'ターゲットキーワード',
          example: 'SEO 検索順位 上げる方法',
        },
        {
          name: 'extracted_terms',
          type: 'step_result',
          description: '抽出された重要語句リスト（形態素解析結果）',
          example:
            '[{"term": "検索", "frequency": 4, "pos_tags": ["名詞"], "example_headings": ["検索順位を上げる方法"]}]',
        },
      ],
      // キーワード・LSI分析: メインキーワード + 関連・LSIキーワード + サイト情報
      'keyword_lsi_analysis': [
        {
          name: 'main_keyword',
          type: 'input',
          description: 'メインキーワード',
          example: 'SEO 検索順位 上げる方法',
        },
        {
          name: 'related_keywords',
          type: 'external',
          description: '関連キーワード一覧（実際のDataForSEOデータ）',
          example: '["SEO対策", "検索エンジン最適化", "検索順位向上"]',
        },
        {
          name: 'lsi_keywords',
          type: 'external',
          description: 'LSIキーワード一覧（実際のDataForSEOデータ）',
          example: '["Google", "アルゴリズム", "コンテンツ", "バックリンク"]',
        },
        {
          name: 'site_url',
          type: 'input',
          description: '分析対象サイトのURL',
          example: 'https://example.com/seo-guide',
        },
        {
          name: 'site_title',
          type: 'input',
          description: '分析対象サイトのタイトル',
          example: 'SEO完全ガイド - 検索順位を上げる方法',
        },
        {
          name: 'site_headings',
          type: 'external',
          description: 'サイトの見出し構造データ（実際のスクレイピング結果）',
          example:
            '[{"level": 1, "text": "SEO基本知識"}, {"level": 2, "text": "キーワード選定"}]',
        },
      ],
      // トピック評価: 個別トピック + 各上位サイトのH1～H4構造化見出しデータ
      'topic_evaluation': [
        {
          name: 'keyword',
          type: 'input',
          description: 'ターゲットキーワード',
          example: 'WordPress テーマ おすすめ',
        },
        {
          name: 'topic',
          type: 'input',
          description:
            '評価対象の個別トピック（MECE整理から一つずつ取り出してバッチ処理）',
          example: '基本知識',
        },
        {
          name: 'structured_headings_data',
          type: 'external',
          description: '各上位サイトのH1～H4構造化見出しデータ',
          example: '{"sites": [{"headings_structure": [...]}]}',
        },
      ],
      // 各サイト概要生成: 競合サイトの基本情報から概要を生成
      'site_summary_generation': [
        {
          name: 'keyword',
          type: 'input',
          description: '検索キーワード',
          example: 'ブログ 収益化',
        },
        {
          name: 'site_url',
          type: 'external',
          description: 'サイトURL',
          example: 'https://example.com/article',
        },
        {
          name: 'site_title',
          type: 'external',
          description: 'ページタイトル',
          example: '一般人のブログ収入の平均はどれくらい？',
        },
        {
          name: 'site_domain',
          type: 'external',
          description: 'サイトのドメイン名（サイト名として使用）',
          example: 'example.com',
        },
        {
          name: 'site_headings',
          type: 'external',
          description: 'サイトの見出し構成（WebScraperで取得）',
          example:
            '[{"text": "ブログ収益化とは", "level": 2}, {"text": "平均収入", "level": 3}]',
        },
        {
          name: 'unified_topics',
          type: 'step_result',
          description: '統合されたトピック一覧',
          example: '["収益化方法", "平均収入", "始め方"]',
        },
      ],
      // 検索意図の深掘り分析: 検索意図を3つの要素で構造化分析
      'search_intent_deepdive': [
        {
          name: 'keyword',
          type: 'input',
          description: '分析対象の検索キーワード',
          example: 'ブログ 収益化',
        },
        {
          name: 'analysis_timestamp',
          type: 'context',
          description: '分析実行日時',
          example: '2025-07-30T12:00:00Z',
        },
        {
          name: 'competitor_titles',
          type: 'external',
          description: '上位サイトタイトル一覧',
          example: '["タイトル1", "タイトル2", "タイトル3"]',
        },
        {
          name: 'unified_topics',
          type: 'step_result',
          description: '統合されたトピック一覧',
          example: '["収益化方法", "平均収入", "始め方"]',
        },
        {
          name: 'people_also_ask',
          type: 'external',
          description: 'People Also Ask情報',
          example: '["関連質問1", "関連質問2"]',
        },
        {
          name: 'serp_features',
          type: 'external',
          description: 'SERP特徴情報',
          example: '{"ai_overview": true, "featured_snippet": false}',
        },
      ],
      // AIOコンテンツ戦略生成: 分析結果統合データ
      'aio_content_strategy_generation': [
        {
          name: 'keyword',
          type: 'input',
          description: 'ターゲットキーワード（分析対象のメインクエリ）',
          example: '塾講師 年収',
        },
        {
          name: 'search_intent_analysis',
          type: 'step_result',
          description: '検索意図分析結果（JSON形式）',
          example:
            '{"primary_need": "...", "core_needs": {...}, "emotional_factors": {...}}',
        },
        {
          name: 'unified_topics_with_scores',
          type: 'step_result',
          description: '統合トピック一覧（スコア付き、JSON配列）',
          example:
            '[{"topic": "平均年収", "relevance_score": 0.9, "competition_level": "high"}]',
        },
        {
          name: 'aio_structure_analysis',
          type: 'step_result',
          description: 'AI Overview構造分析結果（JSON形式）',
          example:
            '{"has_ai_overview": true, "structure": {...}, "optimization_opportunities": [...]}',
        },
        {
          name: 'competitor_analysis_summary',
          type: 'step_result',
          description: '競合サイト分析サマリー（JSON形式）',
          example:
            '{"top_competitors": [...], "content_gaps": [...], "strengths": [...]}',
        },
        {
          name: 'related_keywords',
          type: 'step_result',
          description: '関連キーワード一覧（JSON配列）',
          example: '["塾講師 正社員", "塾講師 アルバイト", "塾講師 転職"]',
        },
        {
          name: 'lsi_keywords',
          type: 'step_result',
          description: 'LSIキーワード一覧（JSON配列）',
          example: '["年収アップ", "給料", "収入", "待遇", "昇進"]',
        },
      ],
      // ファクトチェック: タイトル生成
      'fact_check_title_generation': [
        {
          name: 'text',
          type: 'input',
          description: 'ファクトチェック対象の原稿テキスト',
          example: '近年、AIの発展により...',
        },
      ],
      // ファクトチェック: 主張抽出
      'fact_check_statement_extraction': [
        {
          name: 'text',
          type: 'input',
          description: 'ファクトチェック対象の原稿テキスト',
          example: '近年、AIの発展により...',
        },
        {
          name: 'blocks',
          type: 'input',
          description: '原稿のブロック分割情報（JSON形式）',
          example: '[{"block_index": 0, "text": "...", "html": "..."}, ...]',
        },
        {
          name: 'max_fragments',
          type: 'input',
          description: '最大抽出フラグメント数',
          example: '30',
        },
      ],
      // ファクトチェック: 主張最適化
      'fact_check_statement_optimization': [
        {
          name: 'statements',
          type: 'step_result',
          description: '抽出された主張リスト（JSON形式）',
          example: '[{"statement": "...", "block_index": 0, ...}, ...]',
        },
        {
          name: 'text',
          type: 'input',
          description: '原稿の全文テキスト',
          example: '近年、AIの発展により...',
        },
        {
          name: 'max_fragments',
          type: 'input',
          description: '最大フラグメント数',
          example: '30',
        },
      ],
      // ファクトチェック: 事実検証
      'fact_check_verification': [
        {
          name: 'statement',
          type: 'input',
          description: '検証対象の主張',
          example: 'ChatGPTは2022年11月にリリースされた',
        },
        {
          name: 'context_info',
          type: 'input',
          description:
            'コンテキスト情報（タイトル、見出し、段落文を含む構造化テキスト）',
          example: '【原稿タイトル】AI技術の進化...【大見出し】AIの歴史...',
        },
        {
          name: 'title',
          type: 'context',
          description: '原稿のタイトル（context_info構築用）',
          example: 'AI技術の進化と未来',
        },
        {
          name: 'h2_heading',
          type: 'context',
          description: '主張が含まれるH2見出し（context_info構築用）',
          example: 'AIの歴史',
        },
        {
          name: 'h3_heading',
          type: 'context',
          description: '主張が含まれるH3見出し（context_info構築用）',
          example: '大規模言語モデルの登場',
        },
        {
          name: 'block_text',
          type: 'context',
          description: '主張を含む段落の全文（context_info構築用）',
          example: 'ChatGPTは2022年11月にリリースされ...',
        },
      ],
    };

    return (
      stepMapping[promptId] || [
        {
          name: 'keyword',
          type: 'input',
          description: 'ターゲットキーワード',
          example: 'WordPress テーマ おすすめ',
        },
        {
          name: 'search_location',
          type: 'input',
          description: '検索地域',
          example: 'Japan',
        },
        {
          name: 'search_language',
          type: 'input',
          description: '検索言語',
          example: 'ja',
        },
        {
          name: 'target_audience',
          type: 'input',
          description: 'ターゲット読者',
          example: 'ブログ初心者',
        },
        {
          name: 'content_length',
          type: 'input',
          description: '記事の長さ',
          example: '3000-5000文字',
        },
        {
          name: 'dataforseo_serp',
          type: 'external',
          description: 'DataForSEO SERP結果',
          example: '{"organic": [...], "ai_overview": {...}}',
        },
        {
          name: 'dataforseo_content',
          type: 'external',
          description: 'DataForSEOコンテンツ分析',
          example: '{"headings": [...], "keywords": [...]}',
        },
        {
          name: 'sonar_pro_analysis',
          type: 'external',
          description: 'Sonar-pro HTML解析',
          example: '{"structure": {...}, "topics": [...]}',
        },
      ]
    );
  };

  // 変数情報を取得
  const getVariableInfo = variableName => {
    // 全ての使用可能変数のマスターリスト
    const allVariables = [
      // 基本入力変数
      {
        name: 'keyword',
        type: 'input',
        description: 'ターゲットキーワード',
        example: 'WordPress テーマ おすすめ',
      },
      {
        name: 'search_location',
        type: 'input',
        description: '検索地域',
        example: 'Japan',
      },
      {
        name: 'search_language',
        type: 'input',
        description: '検索言語',
        example: 'ja',
      },
      {
        name: 'search_intent',
        type: 'input',
        description: '検索意図',
        example: '比較検討',
      },
      {
        name: 'target_audience',
        type: 'input',
        description: 'ターゲット読者',
        example: 'ブログ初心者',
      },
      {
        name: 'content_length',
        type: 'input',
        description: '記事の長さ',
        example: '3000-5000文字',
      },

      // 外部API変数
      {
        name: 'competitor_analysis',
        type: 'external',
        description: 'DataForSEO競合分析データ',
        example: '{"competitors": [...], "serp_data": {...}}',
      },
      {
        name: 'dataforseo_serp',
        type: 'external',
        description: 'DataForSEO SERP結果',
        example: '{"organic": [...], "ai_overview": {...}}',
      },
      {
        name: 'dataforseo_content',
        type: 'external',
        description: 'DataForSEOコンテンツ分析',
        example: '{"headings": [...], "keywords": [...]}',
      },
      {
        name: 'sonar_pro_analysis',
        type: 'external',
        description: 'Sonar-pro HTML解析',
        example: '{"structure": {...}, "topics": [...]}',
      },

      // ステップ結果変数
      {
        name: 'raw_topics_data',
        type: 'step_result',
        description: '生トピック抽出結果',
        example: '[{"topic": "基本知識", "relevance": 0.8}]',
      },
      {
        name: 'extracted_topics',
        type: 'step_result',
        description: '抽出されたトピック一覧',
        example: '[{"topic": "基本知識", "strength": 5, "priority": "high"}]',
      },
      {
        name: 'search_intent_analysis',
        type: 'step_result',
        description: '検索意図解析結果',
        example: '{"intent": "比較検討", "query_type": "commercial"}',
      },
    ];

    return allVariables.find(v => v.name === variableName);
  };

  // テンプレートエリアに変数を挿入
  const insertVariable = variableName => {
    if (templateRef.current) {
      const textarea = templateRef.current.resizableTextArea.textArea;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const currentValue = editForm.getFieldValue('template') || '';

      const newValue =
        currentValue.substring(0, start) +
        `{${variableName}}` +
        currentValue.substring(end);

      editForm.setFieldsValue({ template: newValue });

      // カーソル位置を調整
      const newPosition = start + variableName.length + 2;
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newPosition, newPosition);
      }, 0);
    }
  };

  // テンプレートから変数を抽出
  const extractVariables = template => {
    if (!template) return [];
    // 完全な {変数名} パターンのみを抽出（単語文字のみ）
    const matches = template.match(/\{([a-zA-Z_][a-zA-Z0-9_]*)\}/g);
    return matches
      ? [...new Set(matches.map(match => match.slice(1, -1)))]
      : [];
  };

  // テスト実行
  const handleTestExecution = async () => {
    try {
      setTestLoading(true);

      // 編集中のフォームデータを取得
      const formValues = editForm.getFieldsValue();
      const currentTemplate = formValues.template || '';
      const currentModel = formValues.model || 'gpt-4.1';

      // テスト用のプロンプトテンプレートを作成（変数代入済み）
      let filledPrompt = currentTemplate;
      Object.entries(testVariables).forEach(([key, value]) => {
        if (value && value.trim()) {
          filledPrompt = filledPrompt.replace(
            new RegExp(`\\{${key}\\}`, 'g'),
            value,
          );
        }
      });

      // テスト実行データを準備
      const testData = {
        variables: testVariables,
        use_real_data: useRealData,
        analysis_id: selectedAnalysisId,
        // 編集中のテンプレートとモデルを含める
        override_template: currentTemplate,
        override_model: currentModel,
        // 保存前でも編集中の thinking / effort でテストできるようにする
        // （テストと本番で違う設定が動くのを避ける）
        override_model_options: buildModelOptions(formValues),
      };

      const result = await v2PromptsApi.testPrompt(
        selectedPrompt.prompt_id,
        testData,
      );

      console.log('テスト結果:', result);
      console.log('variables_used:', result.variables_used);
      console.log('testVariables:', testVariables);

      setTestResult({
        success: true,
        result: result.result,
        model: result.model || currentModel, // 実際に使用されたモデル
        executionTime: result.execution_time || 0,
        timestamp: result.timestamp,
        filledPrompt: filledPrompt, // フロントエンドで作成した代入済みプロンプト
        variables_used: result.variables_used, // バックエンドから返された実際の変数データ
      });

      message.success('テスト実行が完了しました');
    } catch (error) {
      console.error('テスト実行エラー:', error);
      setTestResult({
        success: false,
        error:
          error.response?.data?.detail ||
          error.message ||
          'テスト実行に失敗しました',
      });
      message.error('テスト実行に失敗しました');
    } finally {
      setTestLoading(false);
    }
  };

  // テスト変数値変更
  const handleTestVariableChange = (variable, value) => {
    setTestVariables(prev => ({
      ...prev,
      [variable]: value,
    }));
  };

  // 分析データ一覧取得
  const loadAnalysisDataList = async () => {
    if (!useRealData) return;

    setLoadingAnalysisData(true);
    try {
      const response = await v2PromptsApi.getAnalysisDataList();
      setAnalysisDataList(response.analyses || []);
    } catch (error) {
      message.error('分析データの取得に失敗しました');
      console.error('分析データ取得エラー:', error);
    } finally {
      setLoadingAnalysisData(false);
    }
  };

  // useRealDataが変更された時に分析データを取得
  useEffect(() => {
    if (useRealData) {
      loadAnalysisDataList();
    } else {
      setAnalysisDataList([]);
      setSelectedAnalysisId(null);
    }
  }, [useRealData]); // loadAnalysisDataListを依存関係から削除

  // スタイル一覧取得
  const loadStyles = async () => {
    setLoadingStyles(true);
    try {
      const response = await v2StylesApi.getStyles({ params: { limit: 200 } });
      const styles = response?.items || response || [];
      console.log('[PromptManagement] Loaded styles:', styles);
      console.log('[PromptManagement] First style:', styles[0]);
      setStyleList(styles);
    } catch (error) {
      message.error('スタイル一覧の取得に失敗しました');
      console.error('スタイル取得エラー:', error);
    } finally {
      setLoadingStyles(false);
    }
  };

  // 分析結果選択時の変数自動入力
  const handleAnalysisSelect = async analysisId => {
    if (!analysisId) {
      setSelectedAnalysisId(null);
      return;
    }

    setSelectedAnalysisId(analysisId);

    try {
      const variables =
        await v2PromptsApi.getAnalysisPromptVariables(analysisId);

      // 変数を自動入力
      setTestVariables(prev => ({
        ...prev,
        keyword: variables.keyword || prev.keyword,
        searchIntentAnalysis: variables.searchIntentAnalysis
          ? JSON.stringify(variables.searchIntentAnalysis, null, 2)
          : prev.searchIntentAnalysis,
        topicMap: variables.topicMap
          ? JSON.stringify(variables.topicMap, null, 2)
          : prev.topicMap,
        competitorHeadings: variables.competitorHeadings
          ? JSON.stringify(variables.competitorHeadings, null, 2)
          : prev.competitorHeadings,
      }));

      message.success('分析結果から変数を読み込みました');
    } catch (error) {
      message.error('分析結果の読み込みに失敗しました');
      console.error('分析変数読み込みエラー:', error);
    }
  };

  // スタイル選択時の変数自動入力
  const handleStyleSelect = async styleId => {
    if (!styleId) {
      setSelectedStyleId(null);
      return;
    }

    setSelectedStyleId(styleId);

    try {
      const styleData = await v2PromptsApi.getStylePromptFormat(styleId);

      // 変数を自動入力
      setTestVariables(prev => ({
        ...prev,
        style: styleData.style
          ? JSON.stringify(styleData.style, null, 2)
          : prev.style,
      }));

      message.success('スタイルから変数を読み込みました');
    } catch (error) {
      message.error('スタイルの読み込みに失敗しました');
      console.error('スタイル変数読み込みエラー:', error);
    }
  };

  // コンポーネントマウント時にスタイル一覧を取得
  useEffect(() => {
    loadStyles();
  }, []);

  // プロンプトテーブルのカラム定義
  const promptColumns = [
    {
      title: 'プロンプト名',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        const currentVersion = record.versions?.find(
          v => v.version === record.current_version,
        );
        return (
          <Space direction="vertical" size={2}>
            <Text strong>{text}</Text>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {currentVersion?.model || 'Unknown'} • v{record.current_version}
            </Text>
          </Space>
        );
      },
    },
    {
      title: '最終実行',
      key: 'lastExecution',
      width: 150,
      render: (_, record) => {
        const lastExec = record.last_executions?.[0];
        if (!lastExec) return <Text type="secondary">未実行</Text>;

        return (
          <Space direction="vertical" size={2}>
            <Text style={{ fontSize: '12px' }}>
              {formatDate(lastExec.executed_at)}
            </Text>
            <Space>
              {lastExec.status === 'completed' ? (
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
              )}
              <Text style={{ fontSize: '12px' }}>
                {Math.round(lastExec.execution_time)}s
              </Text>
              {(() => {
                const cost = lastExec.cost || lastExec.token_usage?.total_cost;
                return cost ? (
                  <Text style={{ fontSize: '11px', color: '#722ed1' }}>
                    ¥{cost.toFixed(2)}
                  </Text>
                ) : null;
              })()}
            </Space>
          </Space>
        );
      },
    },
    {
      title: 'バージョン',
      key: 'versions',
      width: 180,
      render: (_, record) => {
        const currentVersion = record.versions?.find(
          v => v.version === record.current_version,
        );
        return (
          <Space direction="vertical" size={2}>
            <Space>
              <Tag color="blue">v{record.current_version}</Tag>
              <Badge status="success" text="アクティブ" />
            </Space>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {record.versions?.length || 0}個のバージョン
            </Text>
          </Space>
        );
      },
    },
  ];

  // バージョン削除機能を追加
  const handleDeleteVersion = async version => {
    try {
      await v2PromptsApi.deleteVersion(
        selectedPrompt.prompt_id,
        version.version,
      );
      message.success(`バージョンv${version.version}を削除しました`);
      await reloadSelectedPrompt(selectedPrompt.prompt_id); // バージョンリストを更新
      // 削除したバージョンがアクティブだった場合、フォームを更新
      if (version.is_active) {
        const updatedPrompts = await v2PromptsApi.getPrompts();
        const updatedPrompt = updatedPrompts.find(
          p => p.prompt_id === selectedPrompt.prompt_id,
        );
        if (updatedPrompt) {
          handleRowClick(updatedPrompt);
        }
      }
    } catch (error) {
      console.error('Error deleting version:', error);
      message.error('バージョン削除に失敗しました');
    }
  };

  // 日付の安全な表示関数を追加（既存パターンに合わせてmoment-timezone使用）
  const formatDate = dateString => {
    if (!dateString) {
      console.log('🕐 formatDate: dateString is null or undefined');
      return '-';
    }
    try {
      console.log(
        '🕐 formatDate input:',
        dateString,
        'type:',
        typeof dateString,
      );
      // バックエンドから返される日付は既にJST（+09:00）形式のため、そのまま解析
      const result = moment(dateString).format('YYYY/MM/DD HH:mm');
      console.log('🕐 formatDate result:', result);
      return result;
    } catch (error) {
      console.error(
        '🕐 Date formatting error:',
        error,
        'for input:',
        dateString,
      );
      return '-';
    }
  };

  // 権限チェック中の表示
  if (checkingPermission) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Spin size="large" />
        <Text style={{ display: 'block', marginTop: 16 }}>
          権限を確認しています...
        </Text>
      </div>
    );
  }

  // 権限がない場合の表示
  if (!hasPermission) {
    return (
      <div style={{ padding: '24px', textAlign: 'center' }}>
        <Alert
          message="アクセス権限がありません"
          description="プロンプト管理機能はマスター権限を持つユーザーのみご利用いただけます。"
          type="error"
          showIcon
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      {/* ヘッダー */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>
            <RobotOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            プロンプト管理
          </Title>
          <Text type="secondary">カテゴリー別の整理と管理</Text>
        </Col>
      </Row>

      <Row gutter={24}>
        {/* 左側: カテゴリー */}
        <Col xs={24} lg={8}>
          <Card title="カテゴリー" style={{ height: 'fit-content' }}>
            <Timeline>
              {GENERATION_FLOW.map(cat => (
                <Timeline.Item
                  key={cat.id}
                  color={selectedStep === cat.id ? '#1890ff' : '#d9d9d9'}
                  dot={selectedStep === cat.id ? <ApiOutlined /> : null}
                >
                  <div
                    style={{
                      cursor: 'pointer',
                      padding: '8px',
                      borderRadius: '4px',
                      backgroundColor:
                        selectedStep === cat.id ? '#f0f8ff' : 'transparent',
                    }}
                    onClick={() => setSelectedStep(cat.id)}
                  >
                    <Text strong={selectedStep === cat.id}>{cat.name}</Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      {cat.description}
                    </Text>
                    <br />
                    <Text type="secondary" style={{ fontSize: '12px' }}>
                      プロンプト数: {cat.prompts.length}
                    </Text>
                  </div>
                </Timeline.Item>
              ))}
            </Timeline>
          </Card>
        </Col>

        {/* 右側: プロンプト詳細 */}
        <Col xs={24} lg={16}>
          <Card
            title={
              <Space>
                <CodeOutlined />
                {GENERATION_FLOW.find(s => s.id === selectedStep)?.name}{' '}
                のプロンプト
              </Space>
            }
            extra={null}
          >
            <Table
              columns={promptColumns}
              dataSource={getPromptsForStep(selectedStep)}
              rowKey="id"
              pagination={false}
              onRow={record => ({
                onClick: () => handleRowClick(record),
                style: { cursor: 'pointer' },
              })}
              locale={{
                emptyText: <Empty description="プロンプトがありません" />,
              }}
            />
          </Card>
        </Col>
      </Row>

      {/* 統合プロンプト管理モーダル */}
      <Modal
        title={
          <Space>
            <CodeOutlined />
            {selectedPrompt?.name}
            <Tag color="blue">v{selectedPrompt?.current_version}</Tag>
          </Space>
        }
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        width="calc(100vw - 40px)"
        style={{
          top: 20,
          maxWidth: '1600px',
          margin: '0 auto',
        }}
        bodyStyle={{
          height: 'calc(100vh - 160px)',
          overflow: 'auto', // スクロール可能に変更
          padding: 0,
        }}
        footer={null}
        destroyOnClose
      >
        <div
          style={{
            height: '100%',
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            style={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
            tabBarStyle={{
              margin: '0 16px',
              flexShrink: 0,
            }}
          >
            {/* 編集タブ */}
            <TabPane tab="プロンプト編集" key="edit">
              <div
                style={{
                  height: 'calc(100vh - 220px)',
                  overflow: 'auto',
                  padding: '0 16px',
                }}
              >
                <Row gutter={16} style={{ height: '100%' }}>
                  {/* 左側: プロンプト編集フォーム */}
                  <Col span={16}>
                    <Form
                      form={editForm}
                      layout="vertical"
                      onFinish={handleSavePrompt}
                      style={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="name"
                            label="プロンプト名"
                            rules={[
                              {
                                required: true,
                                message: 'プロンプト名を入力してください',
                              },
                            ]}
                          >
                            <Input />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            name="model"
                            label="使用モデル"
                            rules={[
                              {
                                required: true,
                                message: 'モデルを選択してください',
                              },
                            ]}
                          >
                            {/*
                              モデルを追加・廃止したら backend/tools/verify_model_registry.py を実行して、
                              llm_proxy / token_pricing / LLMModel Enum との整合を必ず確認すること。
                              提供終了したモデルは行ごと消さず disabled で残す
                              （既存プロンプトが選択中の場合に何が起きたか分からなくなるため）。
                            */}
                            <Select>
                              {/* Anthropic */}
                              <Option value="claude-opus-5">
                                Claude Opus 5 ($5/$25 per MTok)
                              </Option>
                              <Option value="claude-sonnet-5">
                                Claude Sonnet 5 ($2/$10 per MTok・2026/8/31まで導入価格)
                              </Option>
                              <Option value="claude-sonnet-4-6">
                                Claude Sonnet 4.6 ($3/$15 per MTok)
                              </Option>
                              <Option value="claude-opus-4-6">
                                Claude Opus 4.6 ($5/$25 per MTok)
                              </Option>
                              <Option value="claude-sonnet-4-5-20250929">
                                Claude Sonnet 4.5 ($3/$15 per MTok)
                              </Option>
                              <Option value="claude-haiku-4-5-20251001">
                                Claude Haiku 4.5 ($1/$5 per MTok)
                              </Option>
                              <Option value="claude-opus-4-5-20251101">
                                Claude Opus 4.5 ($5/$25 per MTok)
                              </Option>
                              <Option value="claude-opus-4-20250514" disabled>
                                Claude Opus 4（提供終了 → Opus 5 へ）
                              </Option>
                              {/* OpenAI */}
                              <Option value="gpt5">GPT-5</Option>
                              <Option value="gpt5-mini">GPT-5 Mini</Option>
                              <Option value="gpt5-nano">GPT-5 Nano</Option>
                              <Option value="o3">OpenAI o3</Option>
                              <Option value="gpt-4.1">GPT-4.1</Option>
                              <Option value="gpt-4.1-mini">GPT-4.1 Mini</Option>
                              <Option value="gpt-4.1-nano">GPT-4.1 Nano</Option>
                              <Option value="gpt-4o">GPT-4o</Option>
                              <Option value="gpt-4o-mini">GPT-4o Mini</Option>
                              <Option value="gpt-3.5-turbo">
                                GPT-3.5 Turbo（2026/10/23 提供終了予定）
                              </Option>
                              {/* Google Gemini */}
                              <Option value="gemini-3.6-flash">
                                Gemini 3.6 Flash ($1.5/$7.5 per MTok)
                              </Option>
                              <Option value="gemini-3.5-flash">
                                Gemini 3.5 Flash ($1.5/$9 per MTok)
                              </Option>
                              <Option value="gemini-3.5-flash-lite">
                                Gemini 3.5 Flash-Lite ($0.3/$2.5 per MTok)
                              </Option>
                              <Option value="gemini-3.1-pro-preview">
                                Gemini 3.1 Pro Preview ($2/$12 per MTok)
                              </Option>
                              <Option value="gemini-3-flash-preview">
                                Gemini 3 Flash Preview ($0.5/$3 per MTok)
                              </Option>
                              <Option value="gemini-3.1-flash-lite">
                                Gemini 3.1 Flash-Lite ($0.25/$1.5 per MTok)
                              </Option>
                              <Option value="gemini-2.5-pro">
                                Gemini 2.5 Pro ($1.25/$10 per MTok・2026/10/16 提供終了予定)
                              </Option>
                              <Option value="gemini-2.5-flash">
                                Gemini 2.5 Flash ($0.3/$2.5 per MTok・2026/10/16 提供終了予定)
                              </Option>
                              <Option value="gemini-2.5-flash-lite">
                                Gemini 2.5 Flash-Lite ($0.1/$0.4 per MTok・2026/10/16 提供終了予定)
                              </Option>
                              <Option value="gemini-3-pro-preview" disabled>
                                Gemini 3 Pro Preview（提供終了 → 3.1 Pro Preview へ）
                              </Option>
                              <Option value="gemini-2.0-flash-lite" disabled>
                                Gemini 2.0 Flash Lite（提供終了 → 3.5 Flash-Lite へ）
                              </Option>
                              {/* Perplexity */}
                              <Option value="sonar-reasoning-pro">
                                Sonar Reasoning Pro
                              </Option>
                              <Option value="sonar-pro">Sonar Pro</Option>
                              <Option value="sonar">Sonar</Option>
                              {/* DeepSeek */}
                              <Option value="deepseek-r1" disabled>
                                DeepSeek R1（現在利用できません）
                              </Option>
                            </Select>
                          </Form.Item>
                        </Col>
                      </Row>

                      {/*
                        thinking / effort の設定。
                        対応表はバックエンド（/api/v2/prompts/model-capabilities）が正典で、
                        対応していないモデルを選んでいる間は欄ごと出さない。
                        「有効/無効」の2択にしないのは、モデルによって省略時の既定が逆
                        （Opus 5 系は既定ON、Opus 4.6 系は既定OFF）で意味が定まらないため。
                      */}
                      <Form.Item
                        noStyle
                        shouldUpdate={(prev, cur) =>
                          prev.model !== cur.model ||
                          prev.thinkingMode !== cur.thinkingMode
                        }
                      >
                        {({ getFieldValue }) => {
                          const selectedModel = getFieldValue('model');
                          const caps =
                            modelCapabilities.models?.[selectedModel];
                          if (!caps) return null;

                          const thinkingMode = getFieldValue('thinkingMode');
                          const limit = caps.disable_thinking_max_effort;
                          // 「thinking無効 × 高すぎるeffort」はAPIが400を返す組み合わせなので選べなくする
                          const allowedEffort =
                            thinkingMode === 'disabled' && limit
                              ? caps.effort.slice(
                                  0,
                                  caps.effort.indexOf(limit) + 1,
                                )
                              : caps.effort;

                          return (
                            <Row gutter={16}>
                              {caps.thinking && (
                                <Col span={12}>
                                  <Form.Item
                                    name="thinkingMode"
                                    label="thinking（思考）"
                                    tooltip={
                                      caps.thinking_default === 'adaptive'
                                        ? 'このモデルは指定しない場合、思考が有効になります'
                                        : 'このモデルは指定しない場合、思考は無効です'
                                    }
                                  >
                                    <Select>
                                      <Option value="default">
                                        モデル既定に任せる
                                      </Option>
                                      <Option value="adaptive">
                                        有効（品質重視・遅く高くなります）
                                      </Option>
                                      <Option value="disabled">
                                        無効（速度重視）
                                      </Option>
                                    </Select>
                                  </Form.Item>
                                </Col>
                              )}
                              <Col span={12}>
                                <Form.Item
                                  name="effort"
                                  label="effort（思考の深さ）"
                                  tooltip="上げるほど品質が上がりコストと時間が増えます"
                                >
                                  <Select allowClear placeholder="モデル既定に任せる">
                                    {allowedEffort.map(level => (
                                      <Option key={level} value={level}>
                                        {level}
                                      </Option>
                                    ))}
                                  </Select>
                                </Form.Item>
                              </Col>
                            </Row>
                          );
                        }}
                      </Form.Item>

                      <Form.Item name="memo" label="メモ">
                        <Input.TextArea
                          rows={2}
                          placeholder="このプロンプトの目的や注意事項など"
                        />
                      </Form.Item>

                      <Form.Item
                        name="template"
                        label="プロンプトテンプレート"
                        rules={[
                          {
                            required: true,
                            message: 'テンプレートを入力してください',
                          },
                        ]}
                        style={{ flex: 1, marginBottom: '16px' }}
                      >
                        <TextArea
                          ref={templateRef}
                          rows={15}
                          style={{
                            height: 'calc(100vh - 450px)',
                            minHeight: '300px',
                          }}
                          placeholder="プロンプトテンプレートを入力してください。変数は {変数名} の形式で記述できます。"
                        />
                      </Form.Item>

                      <div
                        style={{
                          flexShrink: 0,
                          padding: '16px 0',
                          borderTop: '1px solid #f0f0f0',
                          backgroundColor: '#fafafa',
                          margin: '0 -16px -16px',
                          paddingLeft: '16px',
                          paddingRight: '16px',
                        }}
                      >
                        <Row gutter={16} align="middle">
                          <Col span={12}>
                            <Form.Item
                              name="isActive"
                              label="アクティブ"
                              valuePropName="checked"
                              style={{ marginBottom: 0 }}
                            >
                              <Switch />
                            </Form.Item>
                          </Col>
                          <Col span={12} style={{ textAlign: 'right' }}>
                            <Space>
                              <Button
                                type="primary"
                                htmlType="submit"
                                icon={<SaveOutlined />}
                              >
                                保存
                              </Button>
                              <Button onClick={handleCreateNewVersion}>
                                新バージョン作成
                              </Button>
                            </Space>
                          </Col>
                        </Row>
                      </div>
                    </Form>
                  </Col>

                  {/* 右側: 使用可能変数一覧 */}
                  <Col span={8}>
                    <Card
                      title="使用可能変数"
                      size="small"
                      style={{ height: '100%' }}
                      bodyStyle={{
                        height: 'calc(100% - 40px)',
                        overflow: 'auto',
                      }}
                    >
                      <Space direction="vertical" style={{ width: '100%' }}>
                        {selectedPrompt &&
                          getAvailableVariables(selectedPrompt.prompt_id).map(
                            variable => {
                              return (
                                <div
                                  key={variable.name}
                                  style={{ marginBottom: 12 }}
                                >
                                  <div style={{ marginBottom: 4 }}>
                                    <Space align="center">
                                      <Button
                                        type="link"
                                        size="small"
                                        onClick={() =>
                                          insertVariable(variable.name)
                                        }
                                        style={{
                                          padding: 0,
                                          height: 'auto',
                                          fontSize: '12px',
                                        }}
                                      >
                                        <Text
                                          strong
                                          style={{ fontSize: '12px' }}
                                        >
                                          {variable.name}
                                        </Text>
                                      </Button>
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        onClick={() => {
                                          navigator.clipboard.writeText(
                                            `{${variable.name}}`,
                                          );
                                          message.success(
                                            '変数名をコピーしました',
                                          );
                                        }}
                                        style={{ marginLeft: 4 }}
                                      />
                                      <Tag
                                        size="small"
                                        color={
                                          variable.type === 'input'
                                            ? 'blue'
                                            : variable.type === 'external'
                                              ? 'green'
                                              : variable.type === 'step_result'
                                                ? 'orange'
                                                : variable.type === 'asset'
                                                  ? 'purple'
                                                  : variable.type === 'config'
                                                    ? 'cyan'
                                                    : variable.type ===
                                                        'context'
                                                      ? 'geekblue'
                                                      : 'default'
                                        }
                                        style={{ fontSize: '9px' }}
                                      >
                                        {variable.type}
                                      </Tag>
                                    </Space>
                                    <Text
                                      type="secondary"
                                      style={{
                                        fontSize: '10px',
                                        display: 'block',
                                      }}
                                    >
                                      {variable.description}
                                    </Text>
                                    {variable.example && (
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: '9px',
                                          display: 'block',
                                          fontStyle: 'italic',
                                        }}
                                      >
                                        例: {variable.example}
                                      </Text>
                                    )}
                                  </div>
                                </div>
                              );
                            },
                          )}
                      </Space>
                    </Card>
                  </Col>
                </Row>
              </div>
            </TabPane>

            {/* プレビュータブ */}
            <TabPane tab="プレビュー" key="preview">
              <div
                style={{
                  height: 'calc(100vh - 220px)',
                  overflow: 'auto',
                  padding: '24px 16px 16px',
                }}
              >
                {selectedPrompt && (
                  <Row gutter={16} style={{ height: '100%' }}>
                    <Col span={8}>
                      <Card
                        title="変数設定"
                        size="small"
                        style={{ height: '100%' }}
                        bodyStyle={{
                          height: 'calc(100% - 40px)',
                          overflow: 'auto',
                        }}
                      >
                        <Space direction="vertical" style={{ width: '100%' }}>
                          {selectedPrompt &&
                            getAvailableVariables(selectedPrompt.prompt_id).map(
                              variable => {
                                return (
                                  <div
                                    key={variable.name}
                                    style={{ marginBottom: 12 }}
                                  >
                                    <div style={{ marginBottom: 4 }}>
                                      <Space align="center">
                                        <Text
                                          strong
                                          style={{ fontSize: '12px' }}
                                        >
                                          {variable.name}
                                        </Text>
                                        <Tag
                                          size="small"
                                          color={
                                            variable.type === 'input'
                                              ? 'blue'
                                              : variable.type === 'external'
                                                ? 'green'
                                                : variable.type ===
                                                    'step_result'
                                                  ? 'orange'
                                                  : variable.type === 'asset'
                                                    ? 'purple'
                                                    : variable.type === 'config'
                                                      ? 'cyan'
                                                      : variable.type ===
                                                          'context'
                                                        ? 'geekblue'
                                                        : 'default'
                                          }
                                          style={{ fontSize: '9px' }}
                                        >
                                          {variable.type}
                                        </Tag>
                                      </Space>
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: '10px',
                                          display: 'block',
                                        }}
                                      >
                                        {variable.description}
                                      </Text>
                                    </div>
                                    <TextArea
                                      rows={3}
                                      value={
                                        variableValues[variable.name] || ''
                                      }
                                      onChange={e =>
                                        handleVariableChange(
                                          variable.name,
                                          e.target.value,
                                        )
                                      }
                                      placeholder={
                                        variable.example ||
                                        `{${variable.name}}の値を入力`
                                      }
                                      style={{ fontSize: '12px' }}
                                    />
                                  </div>
                                );
                              },
                            )}
                        </Space>
                      </Card>
                    </Col>
                    <Col span={16}>
                      <Card
                        title="プレビュー結果"
                        size="small"
                        style={{ height: '100%' }}
                        bodyStyle={{
                          height: 'calc(100% - 40px)',
                          overflow: 'auto',
                        }}
                        extra={
                          <Button
                            size="small"
                            icon={<CopyOutlined />}
                            onClick={() => {
                              navigator.clipboard.writeText(previewContent);
                              message.success('クリップボードにコピーしました');
                            }}
                          >
                            コピー
                          </Button>
                        }
                      >
                        <pre
                          style={{
                            whiteSpace: 'pre-wrap',
                            backgroundColor: '#f8f9fa',
                            padding: '16px',
                            borderRadius: '6px',
                            fontSize: '13px',
                            lineHeight: '1.6',
                            margin: 0,
                            border: '1px solid #e9ecef',
                            height: '100%',
                            overflow: 'auto',
                          }}
                        >
                          {previewContent ||
                            '変数を設定するとプレビューが表示されます'}
                        </pre>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            </TabPane>

            {/* バージョン管理タブ */}
            <TabPane tab="バージョン管理" key="versions">
              <div
                style={{
                  height: 'calc(100vh - 220px)',
                  overflow: 'auto',
                  padding: '24px 16px 16px',
                }}
              >
                {selectedPrompt && (
                  <Row gutter={24}>
                    <Col span={16}>
                      <Card title="バージョン履歴" size="small">
                        <List
                          dataSource={selectedPrompt.versions.sort(
                            (a, b) => b.version - a.version,
                          )}
                          renderItem={version => (
                            <List.Item
                              key={version.version}
                              actions={[
                                version.is_active ? (
                                  <div key="current">
                                    <Tag color="green">現在のバージョン</Tag>
                                    <Button
                                      size="small"
                                      icon={<EyeOutlined />}
                                      onClick={() =>
                                        handlePreviewVersion(version)
                                      }
                                      style={{ marginLeft: 8 }}
                                    >
                                      プレビュー
                                    </Button>
                                  </div>
                                ) : (
                                  <div key="switch">
                                    <Button
                                      size="small"
                                      type="primary"
                                      onClick={() =>
                                        handleSwitchVersion(version)
                                      }
                                    >
                                      切り替え
                                    </Button>
                                    <Button
                                      size="small"
                                      icon={<EyeOutlined />}
                                      onClick={() =>
                                        handlePreviewVersion(version)
                                      }
                                      style={{ marginLeft: 8 }}
                                    >
                                      プレビュー
                                    </Button>
                                  </div>
                                ),
                                <Popconfirm
                                  key="delete"
                                  title="このバージョンを削除しますか？"
                                  onConfirm={() => handleDeleteVersion(version)}
                                  disabled={version.is_active}
                                >
                                  <Button
                                    size="small"
                                    danger
                                    icon={<DeleteOutlined />}
                                    disabled={version.is_active}
                                  />
                                </Popconfirm>,
                              ]}
                            >
                              <List.Item.Meta
                                title={
                                  <Space>
                                    <Text strong>v{version.version}</Text>
                                    <Tag>{version.model}</Tag>
                                    {version.isActive && (
                                      <Tag color="green">アクティブ</Tag>
                                    )}
                                  </Space>
                                }
                                description={
                                  <Space direction="vertical" size={2}>
                                    <Text type="secondary">
                                      {formatDate(version.created_at)} by{' '}
                                      {version.created_by}
                                    </Text>
                                    <Text>{version.changeNote}</Text>
                                    <Text type="secondary">
                                      変数:{' '}
                                      {version.variables &&
                                      typeof version.variables === 'object'
                                        ? Object.keys(version.variables).join(
                                            ', ',
                                          )
                                        : Array.isArray(version.variables)
                                          ? version.variables.join(', ')
                                          : 'なし'}
                                    </Text>
                                  </Space>
                                }
                              />
                            </List.Item>
                          )}
                        />
                      </Card>
                    </Col>
                    <Col span={8}>
                      <Card title="新バージョン作成" size="small">
                        <div style={{ marginBottom: 16 }}>
                          <Text type="secondary" style={{ fontSize: '12px' }}>
                            プロンプトの実験的調整や重要な変更を行う際に、
                            新バージョンを作成することで安全にロールバックできます。
                          </Text>
                        </div>
                        <Form
                          form={versionForm}
                          layout="vertical"
                          onFinish={handleCreateVersionFromTab}
                        >
                          <Form.Item
                            name="changeNote"
                            label="変更内容"
                            rules={[
                              {
                                required: true,
                                message: '変更内容を入力してください',
                              },
                            ]}
                          >
                            <TextArea
                              rows={3}
                              placeholder="何を変更したかを記録してください"
                            />
                          </Form.Item>
                          <Form.Item>
                            <Button
                              type="primary"
                              htmlType="submit"
                              icon={<PlusOutlined />}
                              block
                            >
                              新バージョン作成
                            </Button>
                          </Form.Item>
                        </Form>
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            </TabPane>

            {/* テスト実行タブ */}
            <TabPane tab="テスト実行" key="test">
              <div
                style={{
                  height: 'calc(100vh - 220px)',
                  overflow: 'auto',
                  padding: '24px 16px 16px',
                }}
              >
                {selectedPrompt && (
                  <Row gutter={24} style={{ height: '100%' }}>
                    <Col span={10}>
                      <Card title="テスト変数設定" size="small">
                        <Space
                          direction="vertical"
                          style={{ width: '100%' }}
                          size={16}
                        >
                          {/* DB実データ使用設定 */}
                          <Card
                            size="small"
                            style={{ backgroundColor: '#f8f9fa' }}
                          >
                            <Space
                              direction="vertical"
                              style={{ width: '100%' }}
                            >
                              <div>
                                <Checkbox
                                  checked={useRealData}
                                  onChange={e =>
                                    setUseRealData(e.target.checked)
                                  }
                                >
                                  <Text strong>実際のDBデータを使用</Text>
                                </Checkbox>
                                <Text
                                  type="secondary"
                                  style={{
                                    fontSize: '12px',
                                    display: 'block',
                                    marginLeft: '24px',
                                  }}
                                >
                                  チェックすると、DB内の分析データから変数を自動代入します
                                </Text>
                              </div>

                              {useRealData && (
                                <>
                                  <div>
                                    <Text
                                      style={{
                                        fontSize: '13px',
                                        marginBottom: '8px',
                                        display: 'block',
                                      }}
                                    >
                                      分析データ選択:
                                    </Text>
                                    <Select
                                      style={{ width: '100%' }}
                                      placeholder="分析データを選択してください"
                                      loading={loadingAnalysisData}
                                      value={selectedAnalysisId}
                                      onChange={handleAnalysisSelect}
                                      disabled={analysisDataList.length === 0}
                                      allowClear
                                    >
                                      {analysisDataList.map(analysis => (
                                        <Option
                                          key={analysis.analysis_id}
                                          value={analysis.analysis_id}
                                        >
                                          <Space>
                                            <Text strong>
                                              {analysis.keyword}
                                            </Text>
                                            <Text
                                              type="secondary"
                                              style={{ fontSize: '11px' }}
                                            >
                                              (
                                              {analysis.analysis_id.substring(
                                                0,
                                                8,
                                              )}
                                              ...)
                                            </Text>
                                          </Space>
                                        </Option>
                                      ))}
                                    </Select>
                                    {analysisDataList.length === 0 &&
                                      !loadingAnalysisData && (
                                        <Alert
                                          message="利用可能な分析データがありません"
                                          description="STEP1分析を完了したデータが必要です"
                                          type="info"
                                          size="small"
                                          style={{ marginTop: '8px' }}
                                        />
                                      )}
                                  </div>

                                  <div>
                                    <Text
                                      style={{
                                        fontSize: '13px',
                                        marginBottom: '8px',
                                        display: 'block',
                                      }}
                                    >
                                      スタイル選択:
                                    </Text>
                                    <Select
                                      style={{ width: '100%' }}
                                      placeholder="スタイルを選択してください"
                                      loading={loadingStyles}
                                      value={selectedStyleId}
                                      onChange={handleStyleSelect}
                                      disabled={styleList.length === 0}
                                      allowClear
                                      showSearch
                                      optionFilterProp="children"
                                      filterOption={(input, option) =>
                                        option.children
                                          .toLowerCase()
                                          .includes(input.toLowerCase())
                                      }
                                    >
                                      {styleList.map(style => {
                                        console.log(
                                          '[PromptManagement] Rendering style:',
                                          style,
                                        );
                                        return (
                                          <Option
                                            key={style.id}
                                            value={style.id}
                                          >
                                            {style.style_name ||
                                              style.name ||
                                              `スタイル${style.id}`}
                                            {style.is_default &&
                                              ' (デフォルト)'}
                                          </Option>
                                        );
                                      })}
                                    </Select>
                                    {styleList.length === 0 &&
                                      !loadingStyles && (
                                        <Alert
                                          message="利用可能なスタイルがありません"
                                          description="スタイル管理画面でスタイルを作成してください"
                                          type="info"
                                          size="small"
                                          style={{ marginTop: '8px' }}
                                        />
                                      )}
                                  </div>
                                </>
                              )}
                            </Space>
                          </Card>

                          {/* 変数設定 */}
                          {selectedPrompt &&
                            getAvailableVariables(selectedPrompt.prompt_id).map(
                              variable => {
                                return (
                                  <div key={variable.name}>
                                    <div style={{ marginBottom: 8 }}>
                                      <Space align="center">
                                        <Text strong>{variable.name}</Text>
                                        <Tag
                                          size="small"
                                          color={
                                            variable.type === 'input'
                                              ? 'blue'
                                              : variable.type === 'external'
                                                ? 'green'
                                                : variable.type ===
                                                    'step_result'
                                                  ? 'orange'
                                                  : variable.type === 'asset'
                                                    ? 'purple'
                                                    : variable.type === 'config'
                                                      ? 'cyan'
                                                      : variable.type ===
                                                          'context'
                                                        ? 'geekblue'
                                                        : 'default'
                                          }
                                        >
                                          {variable.type}
                                        </Tag>
                                        {useRealData && selectedAnalysisId && (
                                          <Tag color="green" size="small">
                                            自動代入
                                          </Tag>
                                        )}
                                      </Space>
                                      <Text
                                        type="secondary"
                                        style={{
                                          fontSize: '12px',
                                          display: 'block',
                                        }}
                                      >
                                        {variable.description}
                                      </Text>
                                    </div>
                                    <TextArea
                                      rows={4}
                                      value={testVariables[variable.name] || ''}
                                      onChange={e =>
                                        handleTestVariableChange(
                                          variable.name,
                                          e.target.value,
                                        )
                                      }
                                      placeholder={
                                        useRealData && selectedAnalysisId
                                          ? 'DBデータから自動代入されます'
                                          : variable.example ||
                                            `{${variable.name}}の値を入力してください`
                                      }
                                      disabled={
                                        useRealData && selectedAnalysisId
                                      }
                                      style={{
                                        backgroundColor:
                                          useRealData && selectedAnalysisId
                                            ? '#f5f5f5'
                                            : undefined,
                                      }}
                                    />
                                  </div>
                                );
                              },
                            )}

                          <div
                            style={{ textAlign: 'center', padding: '16px 0' }}
                          >
                            <Button
                              type="primary"
                              size="medium"
                              icon={<PlayCircleOutlined />}
                              loading={testLoading}
                              onClick={handleTestExecution}
                              disabled={
                                !selectedPrompt ||
                                (useRealData && !selectedAnalysisId)
                              }
                            >
                              {testLoading ? 'テスト実行中...' : 'テスト実行'}
                            </Button>
                            {useRealData && !selectedAnalysisId && (
                              <Text
                                type="secondary"
                                style={{
                                  fontSize: '12px',
                                  display: 'block',
                                  marginTop: '8px',
                                }}
                              >
                                分析データを選択してください
                              </Text>
                            )}
                          </div>
                        </Space>
                      </Card>
                    </Col>
                    <Col span={14}>
                      <Card
                        title={
                          <Space>
                            <Text>テスト結果</Text>
                            {testExecutionId && (
                              <Tag color="green">
                                実行ID: {testExecutionId.slice(0, 8)}
                              </Tag>
                            )}
                          </Space>
                        }
                        size="small"
                        bodyStyle={{ overflow: 'auto' }}
                        extra={
                          testResult && (
                            <Button
                              size="small"
                              icon={<CopyOutlined />}
                              onClick={() => {
                                navigator.clipboard.writeText(
                                  testResult.result || testResult,
                                );
                                message.success(
                                  'テスト結果をクリップボードにコピーしました',
                                );
                              }}
                            >
                              コピー
                            </Button>
                          )
                        }
                      >
                        {testLoading ? (
                          <div style={{ textAlign: 'center', padding: '50px' }}>
                            <Spin size="large" />
                            <div style={{ marginTop: 16 }}>
                              <Text>プロンプトを実行中...</Text>
                            </div>
                          </div>
                        ) : testResult ? (
                          <div style={{ marginTop: 16 }}>
                            {testResult.success ? (
                              <div>
                                <Text type="success">
                                  <CheckCircleFilled
                                    style={{ color: '#52c41a' }}
                                  />{' '}
                                  テスト成功
                                </Text>
                                <Divider />
                                <div style={{ marginBottom: 16 }}>
                                  <Space>
                                    <Text strong>使用モデル:</Text>
                                    <Tag color="blue">{testResult.model}</Tag>
                                    <Text strong>実行時間:</Text>
                                    <Text>
                                      {testResult.executionTime
                                        ? testResult.executionTime.toFixed(2)
                                        : '0.00'}
                                      秒
                                    </Text>
                                    <Text strong>実行時刻:</Text>
                                    <Text>
                                      {new Date(
                                        testResult.timestamp,
                                      ).toLocaleString('ja-JP')}
                                    </Text>
                                  </Space>
                                </div>
                                <Collapse
                                  defaultActiveKey={['response']}
                                  size="small"
                                  style={{ marginTop: 16 }}
                                >
                                  {/* 各入力変数を個別のアコーディオンとして表示 */}
                                  {(() => {
                                    const variablesToUse =
                                      testResult.variables_used &&
                                      Object.keys(testResult.variables_used)
                                        .length > 0
                                        ? testResult.variables_used
                                        : testVariables;

                                    // 変数が存在する場合のみ表示
                                    if (
                                      variablesToUse &&
                                      Object.keys(variablesToUse).length > 0
                                    ) {
                                      return Object.entries(variablesToUse).map(
                                        ([key, value]) => (
                                          <Panel header={key} key={key}>
                                            <div
                                              style={{
                                                backgroundColor: '#fafafa',
                                                padding: '12px',
                                                borderRadius: '6px',
                                                border: '1px solid #d9d9d9',
                                                position: 'relative',
                                              }}
                                            >
                                              <Button
                                                type="text"
                                                size="small"
                                                icon={<CopyOutlined />}
                                                style={{
                                                  position: 'absolute',
                                                  top: '8px',
                                                  right: '8px',
                                                  zIndex: 1,
                                                  backgroundColor:
                                                    'rgba(255, 255, 255, 0.9)',
                                                  border: '1px solid #d9d9d9',
                                                  borderRadius: '4px',
                                                  boxShadow:
                                                    '0 1px 2px rgba(0, 0, 0, 0.1)',
                                                }}
                                                onClick={() => {
                                                  const textToCopy =
                                                    typeof value === 'string'
                                                      ? value
                                                      : JSON.stringify(
                                                          value,
                                                          null,
                                                          2,
                                                        );
                                                  navigator.clipboard.writeText(
                                                    textToCopy,
                                                  );
                                                  message.success(
                                                    `${key}の内容をコピーしました`,
                                                  );
                                                }}
                                              />
                                              <pre
                                                style={{
                                                  whiteSpace: 'pre-wrap',
                                                  wordBreak: 'break-word',
                                                  margin: 0,
                                                  fontFamily:
                                                    'Monaco, Menlo, "Ubuntu Mono", monospace',
                                                  fontSize: '13px',
                                                  lineHeight: 1.4,
                                                  paddingRight: '40px', // コピーボタンのスペースを確保
                                                }}
                                              >
                                                {typeof value === 'string'
                                                  ? value
                                                  : JSON.stringify(
                                                      value,
                                                      null,
                                                      2,
                                                    )}
                                              </pre>
                                            </div>
                                          </Panel>
                                        ),
                                      );
                                    }
                                    return null;
                                  })()}
                                  {/* レスポンス（最初から開いた状態） */}
                                  <Panel header="レスポンス" key="response">
                                    <div
                                      style={{
                                        backgroundColor: '#f6f8fa',
                                        padding: '12px',
                                        borderRadius: '6px',
                                        border: '1px solid #e1e4e8',
                                        position: 'relative',
                                      }}
                                    >
                                      <Button
                                        type="text"
                                        size="small"
                                        icon={<CopyOutlined />}
                                        style={{
                                          position: 'absolute',
                                          top: '8px',
                                          right: '8px',
                                          zIndex: 1,
                                          backgroundColor:
                                            'rgba(255, 255, 255, 0.9)',
                                          border: '1px solid #e1e4e8',
                                          borderRadius: '4px',
                                          boxShadow:
                                            '0 1px 2px rgba(0, 0, 0, 0.1)',
                                        }}
                                        onClick={() => {
                                          navigator.clipboard.writeText(
                                            testResult.result,
                                          );
                                          message.success(
                                            'レスポンスの内容をコピーしました',
                                          );
                                        }}
                                      />
                                      <pre
                                        style={{
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          margin: 0,
                                          fontFamily:
                                            'Monaco, Menlo, "Ubuntu Mono", monospace',
                                          fontSize: '13px',
                                          lineHeight: 1.4,
                                          paddingRight: '40px', // コピーボタンのスペースを確保
                                        }}
                                      >
                                        {testResult.result}
                                      </pre>
                                    </div>
                                  </Panel>
                                </Collapse>
                              </div>
                            ) : (
                              <div>
                                <Text type="danger">
                                  <ExclamationCircleFilled
                                    style={{ color: '#ff4d4f' }}
                                  />{' '}
                                  テスト失敗
                                </Text>
                                <Divider />
                                <div
                                  style={{
                                    backgroundColor: '#fff2f0',
                                    padding: '12px',
                                    borderRadius: '6px',
                                    border: '1px solid #ffccc7',
                                  }}
                                >
                                  <Text type="danger">{testResult.error}</Text>
                                </div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Empty
                            description="変数を設定してテスト実行ボタンを押してください"
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                          />
                        )}
                      </Card>
                    </Col>
                  </Row>
                )}
              </div>
            </TabPane>

            {/* 実行履歴タブ */}
            <TabPane tab="実行履歴" key="history">
              <div
                style={{
                  height: 'calc(100vh - 220px)',
                  overflow: 'auto',
                  padding: '24px 16px 16px',
                }}
              >
                {/* 実行履歴統計カード */}
                <Card
                  title="実行統計"
                  size="small"
                  style={{ marginBottom: 16 }}
                >
                  <Row gutter={16}>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#1890ff',
                          }}
                        >
                          {selectedPrompt?.last_executions?.length || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          総実行回数
                        </div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#52c41a',
                          }}
                        >
                          {selectedPrompt?.last_executions?.filter(
                            exec => exec.status === 'completed',
                          ).length || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          成功回数
                        </div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#ff4d4f',
                          }}
                        >
                          {selectedPrompt?.last_executions?.filter(
                            exec => exec.status !== 'completed',
                          ).length || 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          失敗回数
                        </div>
                      </div>
                    </Col>
                    <Col span={6}>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '24px',
                            fontWeight: 'bold',
                            color: '#722ed1',
                          }}
                        >
                          {selectedPrompt?.last_executions?.length > 0
                            ? Math.round(
                                selectedPrompt.last_executions.reduce(
                                  (acc, exec) =>
                                    acc + (exec.execution_time || 0),
                                  0,
                                ) / selectedPrompt.last_executions.length,
                              )
                            : 0}
                          s
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          平均実行時間
                        </div>
                      </div>
                    </Col>
                  </Row>
                  <Row gutter={16} style={{ marginTop: 16 }}>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#1890ff',
                          }}
                        >
                          {selectedPrompt?.last_executions?.length > 0
                            ? Math.round(
                                selectedPrompt.last_executions
                                  .slice(0, 20)
                                  .reduce(
                                    (acc, exec) =>
                                      acc +
                                      (exec.tokens_used ||
                                        exec.token_usage?.total_tokens ||
                                        0),
                                    0,
                                  ) /
                                  Math.min(
                                    20,
                                    selectedPrompt.last_executions.length,
                                  ),
                              ).toLocaleString()
                            : 0}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          平均トークン数（直近20回）
                        </div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#fa8c16',
                          }}
                        >
                          ¥
                          {selectedPrompt?.last_executions?.length > 0
                            ? (
                                selectedPrompt.last_executions
                                  .slice(0, 20)
                                  .reduce(
                                    (acc, exec) =>
                                      acc +
                                      (exec.cost ||
                                        exec.token_usage?.total_cost ||
                                        0),
                                    0,
                                  ) /
                                Math.min(
                                  20,
                                  selectedPrompt.last_executions.length,
                                )
                              ).toFixed(2)
                            : '0.00'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          平均API料金（直近20回）
                        </div>
                      </div>
                    </Col>
                    <Col span={8}>
                      <div style={{ textAlign: 'center' }}>
                        <div
                          style={{
                            fontSize: '20px',
                            fontWeight: 'bold',
                            color: '#52c41a',
                          }}
                        >
                          ¥
                          {selectedPrompt?.last_executions?.length > 0
                            ? selectedPrompt.last_executions
                                .reduce(
                                  (acc, exec) =>
                                    acc +
                                    (exec.cost ||
                                      exec.token_usage?.total_cost ||
                                      0),
                                  0,
                                )
                                .toFixed(2)
                            : '0.00'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          総API料金（全履歴）
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card>

                {/* 実行履歴リスト */}
                {selectedPrompt?.last_executions?.length > 0 ? (
                  <div>
                    <div
                      style={{
                        marginBottom: 16,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <Title level={5} style={{ margin: 0 }}>
                        実行履歴（直近20件）
                      </Title>
                      <Space>
                        <Button
                          size="small"
                          onClick={() => {
                            // 全実行履歴をJSONでダウンロード
                            const dataStr = JSON.stringify(
                              selectedPrompt.last_executions,
                              null,
                              2,
                            );
                            const dataUri =
                              'data:application/json;charset=utf-8,' +
                              encodeURIComponent(dataStr);
                            const exportFileDefaultName = `${selectedPrompt.prompt_id}_execution_history.json`;
                            const linkElement = document.createElement('a');
                            linkElement.setAttribute('href', dataUri);
                            linkElement.setAttribute(
                              'download',
                              exportFileDefaultName,
                            );
                            linkElement.click();
                          }}
                        >
                          履歴をエクスポート
                        </Button>
                      </Space>
                    </div>
                    <List
                      dataSource={selectedPrompt.last_executions}
                      renderItem={(exec, index) => (
                        <List.Item>
                          <Card style={{ width: '100%' }}>
                            <Row gutter={16}>
                              <Col span={6}>
                                <Descriptions
                                  title="実行情報"
                                  size="small"
                                  column={1}
                                >
                                  <Descriptions.Item label="実行時刻">
                                    <Text style={{ fontWeight: 'bold' }}>
                                      {formatDate(exec.executed_at)}
                                    </Text>
                                  </Descriptions.Item>
                                  <Descriptions.Item label="バージョン">
                                    <Tag color="blue">v{exec.version}</Tag>
                                  </Descriptions.Item>
                                  <Descriptions.Item label="モデル">
                                    <Tag color="green">{exec.model_used}</Tag>
                                  </Descriptions.Item>
                                  <Descriptions.Item label="実行時間">
                                    <Text
                                      style={{
                                        color:
                                          exec.execution_time > 30
                                            ? '#ff4d4f'
                                            : exec.execution_time > 10
                                              ? '#fa8c16'
                                              : '#52c41a',
                                        fontWeight: 'bold',
                                      }}
                                    >
                                      {Math.round(exec.execution_time)}秒
                                    </Text>
                                  </Descriptions.Item>
                                  <Descriptions.Item label="トークン数">
                                    <Text style={{ fontWeight: 'bold' }}>
                                      {exec.tokens_used ||
                                        exec.token_usage?.total_tokens ||
                                        'N/A'}
                                    </Text>
                                    {(exec.token_usage?.input_tokens ||
                                      exec.token_usage?.prompt_tokens) &&
                                      (exec.token_usage?.output_tokens ||
                                        exec.token_usage
                                          ?.completion_tokens) && (
                                        <div
                                          style={{
                                            fontSize: '11px',
                                            color: '#666',
                                          }}
                                        >
                                          入力:{' '}
                                          {exec.token_usage.input_tokens ||
                                            exec.token_usage.prompt_tokens}{' '}
                                          / 出力:{' '}
                                          {exec.token_usage.output_tokens ||
                                            exec.token_usage.completion_tokens}
                                        </div>
                                      )}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="API料金">
                                    <Text
                                      style={{
                                        fontWeight: 'bold',
                                        color: '#722ed1',
                                      }}
                                    >
                                      {exec.cost || exec.token_usage?.total_cost
                                        ? `¥${(
                                            exec.cost ||
                                            exec.token_usage?.total_cost ||
                                            0
                                          ).toFixed(2)}`
                                        : 'N/A'}
                                    </Text>
                                    {exec.token_usage?.input_cost &&
                                      exec.token_usage?.output_cost && (
                                        <div
                                          style={{
                                            fontSize: '11px',
                                            color: '#666',
                                          }}
                                        >
                                          入力: ¥
                                          {exec.token_usage.input_cost.toFixed(
                                            2,
                                          )}{' '}
                                          / 出力: ¥
                                          {exec.token_usage.output_cost.toFixed(
                                            2,
                                          )}
                                        </div>
                                      )}
                                  </Descriptions.Item>
                                  <Descriptions.Item label="ステータス">
                                    <Badge
                                      status={
                                        exec.status === 'completed'
                                          ? 'success'
                                          : 'error'
                                      }
                                      text={
                                        exec.status === 'completed'
                                          ? '成功'
                                          : 'エラー'
                                      }
                                    />
                                  </Descriptions.Item>
                                </Descriptions>
                              </Col>
                              <Col span={9}>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 8,
                                  }}
                                >
                                  <Title level={5} style={{ margin: 0 }}>
                                    入力データ
                                  </Title>
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<CopyOutlined />}
                                    onClick={() => {
                                      const inputText = JSON.stringify(
                                        exec.input_data,
                                        null,
                                        2,
                                      );
                                      navigator.clipboard.writeText(inputText);
                                      message.success(
                                        '入力データをコピーしました',
                                      );
                                    }}
                                  >
                                    コピー
                                  </Button>
                                </div>
                                <Collapse size="small" ghost>
                                  <Panel
                                    header="変数データを表示"
                                    key="variables"
                                  >
                                    <pre
                                      style={{
                                        fontSize: '12px',
                                        backgroundColor: '#f5f5f5',
                                        padding: '8px',
                                        maxHeight: '300px',
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        borderRadius: '4px',
                                        border: '1px solid #d9d9d9',
                                      }}
                                    >
                                      {JSON.stringify(
                                        exec.input_data?.variables ||
                                          exec.input_data,
                                        null,
                                        2,
                                      )}
                                    </pre>
                                  </Panel>
                                  {exec.input_data?.filled_prompt && (
                                    <Panel
                                      header="プロンプト全文を表示"
                                      key="filled_prompt"
                                    >
                                      <pre
                                        style={{
                                          fontSize: '12px',
                                          backgroundColor: '#f5f5f5',
                                          padding: '8px',
                                          maxHeight: '300px',
                                          overflow: 'auto',
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          borderRadius: '4px',
                                          border: '1px solid #d9d9d9',
                                        }}
                                      >
                                        {exec.input_data.filled_prompt}
                                      </pre>
                                    </Panel>
                                  )}
                                </Collapse>
                              </Col>
                              <Col span={9}>
                                <div
                                  style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: 8,
                                  }}
                                >
                                  <Title level={5} style={{ margin: 0 }}>
                                    出力結果
                                  </Title>
                                  <Button
                                    size="small"
                                    type="text"
                                    icon={<CopyOutlined />}
                                    onClick={() => {
                                      let output =
                                        typeof exec.output_data === 'string'
                                          ? exec.output_data
                                          : JSON.stringify(
                                              exec.output_data,
                                              null,
                                              2,
                                            );
                                      output = output
                                        .replace(/\\n/g, '\n')
                                        .replace(/\\t/g, '\t');
                                      navigator.clipboard.writeText(output);
                                      message.success(
                                        '出力結果をコピーしました',
                                      );
                                    }}
                                  >
                                    コピー
                                  </Button>
                                </div>
                                <Collapse size="small" ghost>
                                  <Panel header="出力結果を表示" key="output">
                                    <pre
                                      style={{
                                        fontSize: '12px',
                                        backgroundColor: '#f5f5f5',
                                        padding: '8px',
                                        maxHeight: '300px',
                                        overflow: 'auto',
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        borderRadius: '4px',
                                        border: '1px solid #d9d9d9',
                                      }}
                                    >
                                      {(() => {
                                        let output =
                                          typeof exec.output_data === 'string'
                                            ? exec.output_data
                                            : JSON.stringify(
                                                exec.output_data,
                                                null,
                                                2,
                                              );
                                        // JSONのエスケープ文字を実際の改行に変換
                                        return output
                                          .replace(/\\n/g, '\n')
                                          .replace(/\\t/g, '\t');
                                      })()}
                                    </pre>
                                  </Panel>
                                </Collapse>
                              </Col>
                            </Row>
                          </Card>
                        </List.Item>
                      )}
                    />
                  </div>
                ) : (
                  <Empty description="実行履歴がありません" />
                )}
              </div>
            </TabPane>
          </Tabs>
        </div>
      </Modal>

      {/* プレビューモーダル */}
      <Modal
        title={
          previewVersion
            ? `プロンプトプレビュー - v${previewVersion.version}`
            : 'プレビュー'
        }
        visible={previewVisible}
        onCancel={closePreview}
        footer={[
          <Button key="close" onClick={closePreview}>
            閉じる
          </Button>,
        ]}
        width={1000}
      >
        {previewVersion && (
          <div>
            <Row gutter={[16, 16]}>
              <Col span={24}>
                <Card>
                  <Space direction="vertical" style={{ width: '100%' }}>
                    <Title level={5}>バージョン情報</Title>
                    <Text>
                      <strong>バージョン:</strong> v{previewVersion.version}
                    </Text>
                    <Text>
                      <strong>モデル:</strong> {previewVersion.model}
                    </Text>
                    <Text>
                      <strong>作成日:</strong>{' '}
                      {formatDate(previewVersion.created_at)}
                    </Text>
                    <Text>
                      <strong>作成者:</strong> {previewVersion.created_by}
                    </Text>
                    {previewVersion.change_note && (
                      <Text>
                        <strong>変更メモ:</strong> {previewVersion.change_note}
                      </Text>
                    )}
                  </Space>
                </Card>
              </Col>
              <Col span={24}>
                <Card>
                  <Title level={5}>プロンプトテンプレート</Title>
                  <TextArea
                    value={previewVersion.template}
                    rows={20}
                    readOnly
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                </Card>
              </Col>
              {previewVersion.variables &&
                Object.keys(previewVersion.variables).length > 0 && (
                  <Col span={24}>
                    <Card>
                      <Title level={5}>デフォルト変数</Title>
                      <pre
                        style={{
                          backgroundColor: '#f5f5f5',
                          padding: '10px',
                          borderRadius: '4px',
                        }}
                      >
                        {JSON.stringify(previewVersion.variables, null, 2)}
                      </pre>
                    </Card>
                  </Col>
                )}
            </Row>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default PromptManagement;
